// Plan → live-control command translation. Turns the editable parameters a plan
// already holds into concrete vd value-set requests, so the result doubles as a
// dry-run preview (what would be written to hardware) and the payload list for
// the eventual transport. Pure and language-agnostic.
//
// Scope: the PARAMS entries (params.ts) that carry a plan's values; the
// planExternal ones belong to device-setup.ts. Every address emitted for a URX44V
// or a URX22 is a confirmed one. On the URX44 the addresses that are still guesses
// are registered in UNVERIFIED_MAPPINGS below, and a dry-run or write for that
// model carries them.

import type { ConnectionKind, DeviceModel, ModelId } from "../../models/types";
import { parseRef, ref } from "../../models/types";
import type {
  CompParams,
  ConnParams,
  EqBand,
  EqOneKnobParams,
  FxEffectParams,
  GateParams,
  Plan,
  SsmcsBand,
  SsmcsParams,
} from "../plan";
import { incomingConnection, normalizeNodeName, SEND_LEVEL_UNNAMED_DB, SSMCS_INITIAL } from "../plan";
import type { JackSide, NodeParams } from "../plan";
import { connParamContestKey, nodeColorContestKey, nodeParamContestPath } from "../plan-history";
import type { ParamRangeProblem } from "../plan-validate";
import {
  FX_CHANNEL_NODE_INDEX,
  FX_EFFECT_ARRAY_PARAM,
  FX_EFFECT_TYPE_PARAM,
  fxDescRawToSend,
  fxRowOwners,
  resolveFxEffectType,
  fxParams,
  formatHz,
} from "./fx-effect";
import {
  insertFxEngine,
  insertFxFamilyOf,
  insertFxParamKey,
  insertFxSlotRaw,
  insertFxWritableSlots,
  insertFxDeviceDriven,
  insertFxDriverSlots,
} from "./insert-fx-effect";
import { isFixedConnection, monoPairOf, requiresSource, sendTapWritable } from "../routing";
import { jackGainRange } from "../input-lock";
import type { InsertFxOption, ParamName, ParamSpec } from "./params";
import {
  D_GAIN_SOURCES,
  HI_Z_JACKS,
  MIC_LINE_JACK_BASE,
  BUS_TYPE_OPTIONS,
  BUS_TYPE_VARI,
  COMP_EQ_COMP_FIRST,
  COMP_EQ_OPTIONS,
  COMP_EQ_SSMCS,
  COMP_KNEE_DEFAULT,
  COMP_KNEE_OPTIONS,
  COMP_AUTO_MAKEUP_DRIVEN,
  compDeviceDriven,
  DELAY_FRAME_RATE_DEFAULT,
  DELAY_FRAME_RATE_OPTIONS,
  denormalizeInsertFx,
  EQ_BAND_DEFAULT_FREQ_HZ,
  EQ_ONE_KNOB_LEVEL_MAX,
  EQ_ONE_KNOB_LEVEL_MIN,
  EQ_ONE_KNOB_TYPE_ALL_OPTIONS,
  EQ_ONE_KNOB_TYPE_DEFAULT,
  EQ_Q_DEFAULT,
  EQ_TYPE_HIGH_OPTIONS,
  EQ_TYPE_LOW_OPTIONS,
  EQ_TYPE_SHELVING,
  FX_STEREO_ASSIGN_ON,
  planColorIndex,
  INSERT_FX_NONE,
  INSERT_FX_OPTIONS,
  OSC_MODE_OPTIONS,
  OSC_MODE_SINE,
  OUTPUT_INSERT_FX_OPTIONS,
  PAN_BAL_OPTIONS,
  PAN_BAL_PAN,
  paramNameForId,
  PARAMS,
  REC_POINT_DEFAULT,
  REC_POINT_PRE_COMP,
  REC_POINT_PRE_EQ,
  recPointOptionsFor,
  STEREO_ASSIGN_ON_STEREO,
  STEREO_FADER,
  STEREO_ON,
  STEREO_PAN,
} from "./params";
import {
  COMP_RATIO_CH_STEPS,
  COMP_RATIO_FINEST_STEP,
  COMP_RATIO_INF,
  formatCompRatio,
  nearestStepIndex,
} from "./comp-ratio";
import {
  DUCKER_DECAY_FINEST_MS,
  DUCKER_DECAY_STOPS_MS,
  DYN_ATTACK_FINEST_MS,
  DYN_ATTACK_STOPS_MS,
  DYN_HOLD_FINEST_MS,
  DYN_HOLD_STOPS_MS,
  DYN_RELEASE_FINEST_MS,
  DYN_RELEASE_STOPS_MS,
} from "./dyn-time-stops";
import {
  A_GAIN_MAX_DB,
  A_GAIN_MIN_DB,
  attackToVd,
  boolToVd,
  boundRaw,
  burstWidthToVd,
  centiDbToVd,
  clamp,
  D_GAIN_MAX_DB,
  D_GAIN_MIN_DB,
  delayTimeToVd,
  DUCKER_DECAY_MAX_MS,
  DUCKER_DECAY_MIN_MS,
  DYN_ATTACK_MAX_MS,
  DYN_ATTACK_MIN_MS,
  DYN_HOLD_MAX_MS,
  DYN_HOLD_MIN_MS,
  DYN_RATIO_MIN,
  DYN_RELEASE_MAX_MS,
  DYN_RELEASE_MIN_MS,
  EQ_FREQ_MAX_HZ,
  EQ_FREQ_MIN_HZ,
  EQ_GAIN_MAX_DB,
  EQ_GAIN_MIN_DB,
  EQ_Q_MAX,
  EQ_Q_MIN,
  eqFreqToVd,
  eqGainToVd,
  freqToVd,
  gainToVd,
  GATE_RANGE_OFF_DB,
  gateRangeToVd,
  holdToVd,
  HPF_FREQ_MAX_HZ,
  HPF_FREQ_MIN_HZ,
  HPF_FREQ_STEP_HZ,
  levelToVd,
  OSC_LEVEL_MAX_DB,
  OSC_LEVEL_MIN_DB,
  panToVd,
  phonesLevelToVd,
  PORT_REF_NONE,
  qToVd,
  ratioToVd,
  releaseToVd,
  SSMCS_ATTACK_RAW_MAX,
  SSMCS_ATTACK_RAW_MIN,
  SSMCS_COMP_DRIVE_MAX,
  SSMCS_COMP_DRIVE_MIN,
  SSMCS_COMP_INTERNAL_MAX,
  SSMCS_COMP_INTERNAL_MIN,
  SSMCS_EQ_HIGH_FREQ_RAW_MIN,
  SSMCS_EQ_LOW_FREQ_RAW_MAX,
  SSMCS_FREQ_RAW_MAX,
  SSMCS_FREQ_RAW_MIN,
  SSMCS_GAIN_MAX,
  SSMCS_GAIN_MIN,
  SSMCS_MORPHING_MAX,
  SSMCS_MORPHING_MIN,
  SSMCS_Q_RAW_MAX,
  SSMCS_Q_RAW_MIN,
  SSMCS_RATIO_RAW_MAX,
  SSMCS_RATIO_RAW_MIN,
  SSMCS_RELEASE_RAW_MAX,
  SSMCS_RELEASE_RAW_MIN,
  sweetSpotDataToStr,
  tagPortRef,
} from "./vd";

export interface VdCommand {
  /** Catalog parameter this command sets. */
  name: ParamName;
  /** Broker param_id (address first field). */
  paramId: number;
  /** Address x field (0 outside EQ bands). */
  x: number;
  /** Instance index (the address y field). */
  y: number;
  /** Plan-domain value before encoding (dB, pan ±63, or 0/1). */
  planValue: number;
  /** Encoded broker value. */
  vdValue: number;
  /**
   * Owner node id this command's address belongs to (the node a scoped readback
   * would re-read, or whose plan slot a direct-apply writes). Stamped by
   * planToCommands; used to build the device-follow address→node index. Undefined
   * for global, non-node addresses (e.g. sample rate).
   */
  node?: string;
  /**
   * The node-parameter key this command's VALUE came from, `null` where the emit supplied it
   * and ABSENT where nothing recorded one. Present only under `planToCommandOrigins`, which is
   * the only reader; a spread carries it, so the shared-address collapse keeps it.
   */
  origin?: string | null;
  /**
   * Owner nodes whose command for this SAME address carried a DIFFERENT value and
   * was dropped in favour of this one (the last-wins collapse in
   * collapseSharedAddrs). Absent on every command of a plan with no shared
   * address, which is every plan the screens can author.
   */
  shadowed?: string[];
  /**
   * Commands that must be re-sent TOGETHER, in emit order, whenever any one of
   * them differs — because writing an earlier member resets the later ones on the
   * device. Order alone is enough for a full send; it is not enough for the
   * converge loop, which re-sends only what still differs and would otherwise
   * re-arm the reset it is trying to settle (see sendConverging).
   */
  group?: string;
}

function encodeValue(encoding: ParamSpec["encoding"], planValue: number): number {
  switch (encoding) {
    case "level":
      return levelToVd(planValue);
    case "gain":
      return gainToVd(planValue);
    case "pan":
      return panToVd(planValue);
    case "freq":
      return freqToVd(planValue);
    case "enum":
      return planValue;
    case "eqFreq":
      return eqFreqToVd(planValue);
    case "q":
      return qToVd(planValue);
    case "eqGain":
      return eqGainToVd(planValue);
    case "centiDb":
      return centiDbToVd(planValue);
    case "gateRange":
      return gateRangeToVd(planValue);
    case "delayTime":
      return delayTimeToVd(planValue);
    case "phonesLevel":
      return phonesLevelToVd(planValue);
    case "burstWidth":
      return burstWidthToVd(planValue);
    case "attackTime":
      return attackToVd(planValue);
    case "holdTime":
      return holdToVd(planValue);
    case "releaseTime":
      return releaseToVd(planValue);
    case "ratio":
      return ratioToVd(planValue);
    case "portRef":
      return planValue;
    case "portRefTagged":
      return tagPortRef(planValue);
    case "insertFx":
      return denormalizeInsertFx(planValue);
    case "bool":
      return boolToVd(planValue !== 0);
    case "raw":
      return planValue;
  }
}

// Build a command for an explicit param id (used where the id is not a fixed
// registry entry: the stereo-channel block and the per-channel D.Gain).
function rawCommand(
  name: ParamName,
  paramId: number,
  encoding: ParamSpec["encoding"],
  y: number,
  planValue: number,
): VdCommand {
  const vdValue = encodeValue(encoding, planValue);
  return stampOrigin({ name, paramId, x: 0, y, planValue, vdValue }, planValue);
}

/**
 * Where the value a command carries came from, recorded AS the command is built — the one
 * moment both are in hand. Off by default; `planToCommandOrigins` turns it on for one
 * synchronous emit.
 *
 * The reading is not the order of the reads alone. A command is built from a value that was
 * just read, so a FRESH read is that command's key when the two agree — and they can disagree,
 * where an enum resolves an out-of-range number to its own default, which is a value the plan
 * did not supply. A read already spent is reused only for a command carrying the SAME value,
 * which is the shape of a loop sending one value to every linked instance. Anything else is a
 * value the emit supplied itself.
 *
 * Losing track is a third answer, not the second: a command with no `origin` field at all never
 * went through here, and the caller treats that as a value it cannot vouch for rather than as
 * one nobody supplied.
 */
let originCursor: {
  fresh?: { name: string; value: unknown };
  previous?: { name: ParamName; planValue: number; origin: string | null };
} | null = null;

function stampOrigin(command: VdCommand, planValue: number): VdCommand {
  if (!originCursor) return command;
  const fresh = originCursor.fresh;
  originCursor.fresh = undefined;
  let origin: string | null = null;
  if (fresh !== undefined) {
    // A read the emit has not spent yet is this command's: the value is read immediately before
    // the command carrying it is built, and the cursor is cleared at every build, so nothing
    // else can be between them.
    //
    // What the command carries need NOT be what the key holds. The emit inverts (the SSMCS
    // COMP / EQ switches send ON as 0), rounds, and clamps to a range, and every one of those
    // is the value going out under that key — requiring the two to be equal called a document's
    // own SSMCS switch a value nobody chose. The plan holding NOTHING there is the one case
    // that names no key: the emit asks before it decides whether to send one, and then supplies
    // the value itself. That case is a fail-safe rather than a working rule — no command on any
    // model is built right after such a read — and what stands behind it is the contract test,
    // which refuses a command called the emit's own that a leaf can move.
    origin = fresh.value === undefined ? null : fresh.name;
  } else {
    // No read of its own. One value going to every linked instance is a run of commands with
    // the SAME parameter and the same value, so only the one immediately before it can lend a
    // name — matching on the value alone would attribute a value the emit supplies itself to
    // whichever parameter had last been read carrying the same value.
    const before = originCursor.previous;
    origin = before && before.name === command.name && before.planValue === planValue ? before.origin : null;
  }
  originCursor.previous = { name: command.name, planValue, origin };
  return { ...command, origin };
}

function command(name: ParamName, y: number, planValue: number): VdCommand {
  const spec = PARAMS[name];
  return rawCommand(name, spec.id, spec.encoding, y, planValue);
}

/** True for a stereo mixer-channel node id (e.g. "ch_5_6"). */
export function isStereoChannel(nodeId: string): boolean {
  return /^ch_\d+_\d+$/.test(nodeId);
}

/**
 * One polarity-invert (Ф) toggle. Mono channels have a single one; a stereo
 * channel has two independent ones (its L and R sides).
 */
export interface PhaseToggle {
  name: ParamName;
  /** The NodeParams field this toggle reads/writes. */
  key: "phase" | "phaseL" | "phaseR";
  param: number;
  y: number;
  /** "" for a mono channel; "L" / "R" for the two sides of a stereo channel. */
  side: "" | "L" | "R";
}

/**
 * One channel-strip section ON toggle (GATE / COMP / EQ). The broker polarity is
 * mixed, so `onValue` is the raw value that means ON (OFF is its complement).
 */
export interface SectionToggle {
  name: ParamName;
  /** The NodeParams field this toggle reads/writes. */
  key: "gateOn" | "compOn" | "eqOn";
  param: number;
  /** Instance index (the address y field). */
  y: number;
  /** Raw broker value that means ON (the SSMCS comp/eq bank is inverted, so 0). */
  onValue: 0 | 1;
}

export interface ChannelControl {
  fader: number;
  on: number;
  /** → STEREO bus assign ON (post-fader). Distinct from `on` (the channel master). */
  stereoOn: number;
  pan: number;
  /** Rec Point (record / direct-out tap): mono param 137 (input axis), stereo 264. */
  recPoint: number;
  y: number;
  hasHpf: boolean;
  /** A MONO IN channel: its strip carries the dynamics (GATE / COMP->EQ / SSMCS) and the
   *  COMP/EQ type. Its input stage is the jack or source it reads (`inputStageOf`). */
  hasMicStrip: boolean;
  /** Polarity invert: one toggle on a mono channel, two (L/R) on a stereo one. */
  phases: PhaseToggle[];
}

// Stereo channels are indexed by their position among the model's stereo
// channels (which shifts with the mono count). The map is built once per model.
const stereoIndexCache = new WeakMap<DeviceModel, Map<string, number>>();
export function stereoIndexMap(model: DeviceModel): Map<string, number> {
  let map = stereoIndexCache.get(model);
  if (!map) {
    map = new Map();
    let i = 0;
    for (const n of model.nodes) if (n.kind === "channel" && isStereoChannel(n.id)) map.set(n.id, i++);
    stereoIndexCache.set(model, map);
  }
  return map;
}

/**
 * Resolve everything live control needs for a channel node, in one place:
 * fader / ON / pan device params + instance index and whether it has an HPF.
 * Mono channels use 139/140/141/25 at the input index and a single phase (24);
 * stereo channels use the separate 266/267/268 block at the stereo index,
 * independent L/R phase (211/212), and no HPF or MONO IN strip. A channel's input
 * stage is not here: it is the jack or source the channel reads (`inputStageOf`).
 * Null for non-channels.
 */
export function channelControl(model: DeviceModel, nodeId: string): ChannelControl | null {
  if (isStereoChannel(nodeId)) {
    const si = stereoIndexMap(model).get(nodeId);
    if (si === undefined) return null;
    return {
      fader: STEREO_FADER,
      on: STEREO_ON,
      stereoOn: STEREO_ASSIGN_ON_STEREO,
      pan: STEREO_PAN,
      recPoint: PARAMS.REC_POINT_STEREO.id,
      y: si,
      hasHpf: false,
      hasMicStrip: false,
      // Stereo channels invert L and R independently (params 211 / 212).
      phases: [
        { name: "PHASE_L", key: "phaseL", param: PARAMS.PHASE_L.id, y: si, side: "L" },
        { name: "PHASE_R", key: "phaseR", param: PARAMS.PHASE_R.id, y: si, side: "R" },
      ],
    };
  }
  const mono = /^ch(\d+)$/.exec(nodeId);
  if (!mono) return null;
  const y = Number(mono[1]) - 1;
  return {
    fader: PARAMS.CH_FADER.id,
    on: PARAMS.CH_ON.id,
    stereoOn: PARAMS.STEREO_ASSIGN_ON.id,
    pan: PARAMS.CH_PAN.id,
    recPoint: PARAMS.REC_POINT.id,
    y,
    hasHpf: true,
    hasMicStrip: true,
    phases: [{ name: "PHASE", key: "phase", param: PARAMS.PHASE.id, y, side: "" }],
  };
}

/** What a channel's input stage is under the plan's source wire: one jack of a MIC/LINE
 *  input node (its index is the y of the analog input-stage params), a digital source's
 *  D.Gain address, or nothing — no source, or a source with no input stage. */
export type InputStage =
  | { kind: "jack"; node: string; side: JackSide; jack: number; hiZ: boolean }
  | { kind: "digital"; node: string; param: number; instances: readonly number[] }
  | { kind: "none" };

/** The input stage a channel reads. A mono channel on a MIC/LINE node reads the jack its
 *  input slot's parity names (odd slot = the node's second jack), as the source select
 *  writes it; a stereo channel reads the side asked for. */
export function inputStageOf(model: DeviceModel, plan: Plan, channelId: string, side: JackSide = "L"): InputStage {
  const conn = incomingConnection(plan, ref(channelId, "in"), "source");
  if (!conn) return { kind: "none" };
  const source = parseRef(conn.from).nodeId;
  if (!inputPorts(source)) return { kind: "none" };
  const base = MIC_LINE_JACK_BASE[source];
  if (base !== undefined) {
    let read: JackSide = side;
    if (!isStereoChannel(channelId)) {
      const slots = channelInputSlots(model, channelId);
      if (!slots) return { kind: "none" };
      read = slots[0] & 1 ? "R" : "L";
    }
    const jack = base + (read === "R" ? 1 : 0);
    return { kind: "jack", node: source, side: read, jack, hiZ: (HI_Z_JACKS[model.id] ?? []).includes(jack) };
  }
  const address = D_GAIN_SOURCES[source];
  if (address && model.nodes.some((n) => n.id === source)) {
    return { kind: "digital", node: source, param: address.id, instances: address.instances };
  }
  return { kind: "none" };
}

/** The input-stage holders some channel reads: the sides of each MIC/LINE node and the
 *  digital sources. A stereo channel on a MIC/LINE node reads both of its jacks. */
export interface InputReadSet {
  jacks: Map<string, Set<JackSide>>;
  sources: Set<string>;
}
export function inputStageReadSet(model: DeviceModel, plan: Plan): InputReadSet {
  const set: InputReadSet = { jacks: new Map(), sources: new Set() };
  for (const n of model.nodes) {
    if (n.kind !== "channel") continue;
    const sides: JackSide[] = isStereoChannel(n.id) ? ["L", "R"] : ["L"];
    for (const side of sides) {
      const stage = inputStageOf(model, plan, n.id, side);
      if (stage.kind === "jack") {
        const read = set.jacks.get(stage.node) ?? new Set<JackSide>();
        read.add(stage.side);
        set.jacks.set(stage.node, read);
      } else if (stage.kind === "digital") set.sources.add(stage.node);
    }
  }
  return set;
}

/** The channels that read one holder — a MIC/LINE node's side, or a digital source — each
 *  with the side it reads on when it is a stereo channel. */
export function inputStageReaders(
  model: DeviceModel,
  plan: Plan,
  holder: string,
  side?: JackSide,
): Array<{ channel: string; side?: JackSide }> {
  const out: Array<{ channel: string; side?: JackSide }> = [];
  for (const n of model.nodes) {
    if (n.kind !== "channel") continue;
    const stereo = isStereoChannel(n.id);
    for (const s of stereo ? (["L", "R"] as const) : (["L"] as const)) {
      const stage = inputStageOf(model, plan, n.id, s);
      if (stage.kind === "none" || stage.node !== holder) continue;
      if (stage.kind === "jack" && side !== undefined && stage.side !== side) continue;
      out.push(stereo && stage.kind === "jack" ? { channel: n.id, side: stage.side } : { channel: n.id });
      if (stage.kind === "digital") break;
    }
  }
  return out;
}

/** Ids of the model's selectable input nodes — those with a physical port map (INPUT_PORTS). */
export function selectableInputIds(model: DeviceModel): string[] {
  return model.nodes.filter((n) => n.kind === "input" && inputPorts(n.id)).map((n) => n.id);
}

/**
 * Channel-strip section ON toggles (GATE / COMP / EQ) for a channel. MONO IN
 * channels have all three; GATE is type-independent (param 28) but COMP and EQ
 * swap param banks with the COMP/EQ type — COMP->EQ uses 34/44 (1 = on), SSMCS
 * uses 94/106 (0 = on). Stereo channels have only EQ (213). Empty for non-channels.
 */
export function channelSections(model: DeviceModel, nodeId: string, compEqType: number): SectionToggle[] {
  const cc = channelControl(model, nodeId);
  if (!cc) return [];
  if (isStereoChannel(nodeId)) {
    return [{ name: "STEREO_CH_EQ_ON", key: "eqOn", param: PARAMS.STEREO_CH_EQ_ON.id, y: cc.y, onValue: 1 }];
  }
  const ssmcs = compEqType === COMP_EQ_SSMCS;
  return [
    { name: "GATE_ON", key: "gateOn", param: PARAMS.GATE_ON.id, y: cc.y, onValue: 1 },
    ssmcs
      ? { name: "SSMCS_COMP_ON", key: "compOn", param: PARAMS.SSMCS_COMP_ON.id, y: cc.y, onValue: 0 }
      : { name: "COMP_ON", key: "compOn", param: PARAMS.COMP_ON.id, y: cc.y, onValue: 1 },
    ssmcs
      ? { name: "SSMCS_EQ_ON", key: "eqOn", param: PARAMS.SSMCS_EQ_ON.id, y: cc.y, onValue: 0 }
      : { name: "EQ_ON", key: "eqOn", param: PARAMS.EQ_ON.id, y: cc.y, onValue: 1 },
  ];
}

/**
 * A CH → bus send. `level`/`pan`/`on` are instance lists (MIX writes both linked
 * L/R; FX writes a single mono send and has no pan). `tap` is the PRE/POST param.
 */
export interface SendControl {
  y: number;
  level: number[];
  pan: number[];
  on: number[];
  /** PRE/POST tap param (single L slot; the device links R). Every send has one. */
  tap: number;
}

// CH → MIX sends are laid out as 12-param stereo-bus blocks (L slot + R slot, 6
// params each: level/pan/on at offsets 0/1/2, PRE/POST at offset 5 in the L slot
// only). Mono channels use a block based at 146 (y = input index); stereo
// channels a parallel block at 273 (y = stereo index). Confirmed by live scan.
const MIX_SEND_BASE_MONO = 146;
const MIX_SEND_BASE_STEREO = 273;
const MIX_SEND_STRIDE = 12;
const MIX_SEND_BUS_INDEX: Record<string, number> = { "bus.mix1": 0, "bus.mix2": 1 };

// CH → FX sends are 4-param mono blocks: PRE/POST tap at +0, level at +1, on at
// +3 (no pan). Mono channels base at 193 (y = input index); stereo channels at
// 320 (y = stereo index). Confirmed by live scan.
const FX_SEND_BASE_MONO = 193;
const FX_SEND_BASE_STEREO = 320;
const FX_SEND_STRIDE = 4;
const FX_SEND_BUS_INDEX = FX_CHANNEL_NODE_INDEX;

// FX channel → MIX sends. Each MIX is a 10-param block (L slot + R slot, 5 params
// each: PRE/POST tap / level / BAL / on at offsets 0/1/2/3, +4 unused). y = FX
// channel index (the FX bus index, fx1 = 0 / fx2 = 1). L/R are device-linked
// (writing the L slot propagates to R), so the tap is written once on the L slot;
// level/BAL/on write both for parity with CH → MIX. Live snapshot-diff confirmed.
const FXCH_MIX_SEND_BASE = 342;
const FXCH_MIX_SEND_STRIDE = 10; // per MIX bus
const FXCH_MIX_SLOT_STRIDE = 5; // L slot → R slot

/** FX channel instance index (y) for its master fader / BAL / MIX sends, or null
 *  if the node is not an FX channel. FX channels are send sources whose MIX sends
 *  and STEREO main path live outside channelControl (they are buses, not channels);
 *  their instance index is the FX bus index. */
export function fxChannelIndex(nodeId: string): number | null {
  const y = FX_SEND_BUS_INDEX[nodeId];
  return y === undefined ? null : y;
}

/** Send params for a CH/FX-channel → MIX/FX-bus pair, or null if not such a send. */
export function sendControl(model: DeviceModel, channelId: string, busId: string): SendControl | null {
  // FX channel → MIX sends (the FX channels are buses, not channels). The send
  // to STEREO is the fixed main path (FX_CHANNEL_FADER), handled separately.
  const fxY = fxChannelIndex(channelId);
  if (fxY !== null) {
    const mixIndex = MIX_SEND_BUS_INDEX[busId];
    if (mixIndex === undefined) return null;
    const l = FXCH_MIX_SEND_BASE + FXCH_MIX_SEND_STRIDE * mixIndex;
    const r = l + FXCH_MIX_SLOT_STRIDE;
    return { y: fxY, level: [l + 1, r + 1], pan: [l + 2, r + 2], on: [l + 3, r + 3], tap: l };
  }
  const cc = channelControl(model, channelId);
  if (!cc) return null;
  const stereo = isStereoChannel(channelId);
  const mixIndex = MIX_SEND_BUS_INDEX[busId];
  if (mixIndex !== undefined) {
    const base = (stereo ? MIX_SEND_BASE_STEREO : MIX_SEND_BASE_MONO) + MIX_SEND_STRIDE * mixIndex;
    return { y: cc.y, level: [base, base + 6], pan: [base + 1, base + 7], on: [base + 2, base + 8], tap: base + 5 };
  }
  const fxIndex = FX_SEND_BUS_INDEX[busId];
  if (fxIndex !== undefined) {
    const base = (stereo ? FX_SEND_BASE_STEREO : FX_SEND_BASE_MONO) + FX_SEND_STRIDE * fxIndex;
    // PRE/POST tap at base+0 (single mono send, no pan). Live-confirmed writable.
    return { y: cc.y, level: [base + 1], pan: [], on: [base + 3], tap: base };
  }
  return null;
}

/** A bus output fader: which param and the linked instances it writes. */
export interface BusFader {
  name: ParamName;
  param: number;
  instances: number[];
}

// MIX bus output faders share param 674 (level_gain, out axis); each stereo MIX
// occupies an L/R instance pair the device keeps linked. STEREO has its own
// single master fader (581).
export const MIX_FADER_INSTANCES: Record<string, number[]> = {
  "bus.mix1": [0, 1],
  "bus.mix2": [2, 3],
};

/** Output fader for a bus node (STEREO master / MIX), or null if it has none. */
export function busFader(nodeId: string): BusFader | null {
  if (nodeId === "bus.stereo") {
    return { name: "STEREO_MASTER_FADER", param: PARAMS.STEREO_MASTER_FADER.id, instances: [0] };
  }
  const mix = MIX_FADER_INSTANCES[nodeId];
  return mix ? { name: "OUT_FADER", param: PARAMS.OUT_FADER.id, instances: mix } : null;
}

/** Output master balance / pan for a bus node: STEREO master (583, single) or a
 *  MIX bus (676, L/R-linked per MIX), mirroring the fader's instances. Null for
 *  buses with no master balance (FX / monitor / stream / osc). */
export function busBalance(nodeId: string): BusFader | null {
  if (nodeId === "bus.stereo") {
    return { name: "STEREO_MASTER_BAL", param: PARAMS.STEREO_MASTER_BAL.id, instances: [0] };
  }
  const mix = MIX_FADER_INSTANCES[nodeId];
  return mix ? { name: "OUT_MASTER_BAL", param: PARAMS.OUT_MASTER_BAL.id, instances: mix } : null;
}

/** Master ON param/instances for a bus channel: STEREO master (582), a MIX bus
 *  (OUT_MASTER_ON 675, L/R-linked per MIX), or an FX return (FX_CHANNEL_ON, one
 *  instance per FX; the canonical FX-bus ordering), or null for buses with none. */
export function busMasterOn(nodeId: string): { name: ParamName; param: number; instances: number[] } | null {
  if (nodeId === "bus.stereo") {
    return { name: "STEREO_MASTER_ON", param: PARAMS.STEREO_MASTER_ON.id, instances: [0] };
  }
  const mix = MIX_FADER_INSTANCES[nodeId];
  if (mix) return { name: "OUT_MASTER_ON", param: PARAMS.OUT_MASTER_ON.id, instances: mix };
  const fx = FX_SEND_BUS_INDEX[nodeId];
  return fx === undefined ? null : { name: "FX_CHANNEL_ON", param: PARAMS.FX_CHANNEL_ON.id, instances: [fx] };
}

/** EQ-ON param/instances for an output bus: STEREO (498) or MIX (591, L/R-linked). */
export function busEqOn(nodeId: string): { name: ParamName; param: number; instances: number[] } | null {
  if (nodeId === "bus.stereo") {
    return { name: "STEREO_EQ_ON", param: PARAMS.STEREO_EQ_ON.id, instances: [0] };
  }
  const mix = MIX_FADER_INSTANCES[nodeId];
  return mix ? { name: "OUT_EQ_ON", param: PARAMS.OUT_EQ_ON.id, instances: mix } : null;
}

// CH SETTING per-node identity (color + name) shares one addressing scheme: the
// same node kinds carry it on the same instances, only the param id differs by
// attribute. `nodeIdentity` resolves a node to its kind + instances once; color
// and name then pick their param from that kind. Nodes the device does not give
// a CH SETTING (monitor / OSC) resolve to null, so neither attribute is ever
// written to a guessed address.
// Mono and stereo input channels carry their CH SETTING on different blocks: a
// mono channel on the input slot (params 18/20), a stereo channel on the stereo
// index (params 206/208) — the same split the fader/pan block uses (139.. vs
// 266..). So they resolve to distinct identity kinds with different instances.
type IdentityKind = "stereo" | "stream" | "fx" | "mix" | "monoChannel" | "stereoChannel";
function nodeIdentity(model: DeviceModel, nodeId: string): { kind: IdentityKind; instances: number[] } | null {
  if (nodeId === "bus.stereo") return { kind: "stereo", instances: [0] };
  if (nodeId === "bus.stream") return { kind: "stream", instances: [0, 1] };
  // FX is one instance per FX (FX1 = y0, FX2 = y1), reusing the canonical FX-bus
  // ordering rather than a second copy of it.
  const fx = FX_SEND_BUS_INDEX[nodeId];
  if (fx !== undefined) return { kind: "fx", instances: [fx] };
  const mix = MIX_FADER_INSTANCES[nodeId];
  if (mix) return { kind: "mix", instances: mix };
  if (isStereoChannel(nodeId)) {
    const si = stereoIndexMap(model).get(nodeId);
    return si === undefined ? null : { kind: "stereoChannel", instances: [si] };
  }
  const slots = channelInputSlots(model, nodeId);
  return slots ? { kind: "monoChannel", instances: slots } : null;
}

const COLOR_PARAM: Record<IdentityKind, { name: ParamName; param: number }> = {
  stereo: { name: "STEREO_COLOR", param: PARAMS.STEREO_COLOR.id },
  stream: { name: "STREAM_COLOR", param: PARAMS.STREAM_COLOR.id },
  fx: { name: "FX_COLOR", param: PARAMS.FX_COLOR.id },
  mix: { name: "MIX_COLOR", param: PARAMS.MIX_COLOR.id },
  monoChannel: { name: "CH_COLOR", param: PARAMS.CH_COLOR.id },
  stereoChannel: { name: "STEREO_CH_COLOR", param: PARAMS.STEREO_CH_COLOR.id },
};
// Name param ids per kind. These are string-valued (the broker stores them as a
// JSON string), so they live outside the numeric PARAMS catalog and are written
// via the string IPC (vdSetStr) rather than as VdCommands.
const NAME_PARAM: Record<IdentityKind, number> = {
  stereo: 494,
  stream: 702,
  fx: 333,
  mix: 584,
  monoChannel: 18,
  stereoChannel: 206,
};

/** CH SETTING color param/instances for a colorable node (input channels +
 *  STEREO / MIX / FX / STREAMING buses), or null when the device has none. */
export function colorControl(
  model: DeviceModel,
  nodeId: string,
): { name: ParamName; param: number; instances: number[] } | null {
  const id = nodeIdentity(model, nodeId);
  return id ? { ...COLOR_PARAM[id.kind], instances: id.instances } : null;
}

/** CH SETTING name param/instances for a nameable node (same node set as color);
 *  null when the device has no name for it. The value is a string (vdSetStr). */
export function nameControl(model: DeviceModel, nodeId: string): { param: number; instances: number[] } | null {
  const id = nodeIdentity(model, nodeId);
  return id ? { param: NAME_PARAM[id.kind], instances: id.instances } : null;
}

/** One band of a 4-band PEQ: the param ids for each of its values. */
export interface EqBandControl {
  /** 0..3 (LOW / LOW-MID / HIGH-MID / HIGH). */
  index: number;
  name: "low" | "lowMid" | "highMid" | "high";
  on: number;
  /** Filter-type param, or null for the fixed-peaking mid bands. */
  type: number | null;
  q: number;
  freq: number;
  gain: number;
}

/** A 4-band PEQ (input channel or output bus): its bands and the instances each value writes. */
export interface EqControl {
  bands: EqBandControl[];
  instances: number[];
}

/** The four bands, in device order. Exported because every surface that shows a band needs
 *  the same names and the same "which of them carry a filter type" rule. */
export const EQ_BAND_NAMES = ["low", "lowMid", "highMid", "high"] as const;
/** Only LOW and HIGH carry a selectable filter type; the two mid bands are fixed peaking
 *  and the device rejects a type write to them (measured, response_code 400). */
export const eqBandHasType = (index: number): boolean => index === 0 || index === 3;
// Each PEQ band is a 5-param block (on / type / Q / freq / gain) and the first
// band sits 5 params after the EQ-ON anchor. Only the LOW and HIGH bands carry a
// selectable filter type; the two mid bands are fixed peaking.
const EQ_BAND_BASE_OFFSET = 5;
const EQ_BAND_STRIDE = 5;

// Build the four bands from the EQ-ON anchor param (band1 sits 5 params later).
function eqBandsFrom(eqOnParam: number, instances: number[]): EqControl {
  const base = eqOnParam + EQ_BAND_BASE_OFFSET;
  const bands = EQ_BAND_NAMES.map((name, i): EqBandControl => {
    const b = base + EQ_BAND_STRIDE * i;
    return { index: i, name, on: b, type: eqBandHasType(i) ? b + 1 : null, q: b + 2, freq: b + 3, gain: b + 4 };
  });
  return { bands, instances };
}

/**
 * Resolve an output bus 4-band PEQ, or null if the node has none. Reuses the
 * EQ-ON anchor (busEqOn): the band block starts 5 params later and writes the
 * same instances — STEREO a single slot (498→503), MIX the L/R-linked out pair
 * (591→596). Confirmed on STEREO and MIX by live scan (research §12.24).
 */
export function outputEq(nodeId: string): EqControl | null {
  const eq = busEqOn(nodeId);
  return eq ? eqBandsFrom(eq.param, eq.instances) : null;
}

/**
 * Resolve an input channel 4-band PEQ, or null if it has none. Shares the
 * output structure (band block 5 params after the EQ-ON anchor): mono COMP->EQ
 * mode anchors at EQ_ON 44 (→49), stereo channels at STEREO_CH_EQ_ON 213 (→218).
 * SSMCS mode has no 4-band PEQ (the morphing strip replaces it), so it returns
 * null. Confirmed on a mono channel by live scan (research §12.25).
 */
export function inputEq(model: DeviceModel, nodeId: string, compEqType: number): EqControl | null {
  // A mono channel in SSMCS mode has no 4-band PEQ (the morphing strip replaces
  // it); stereo channels always do. Take the EQ-ON anchor from channelSections.
  if (compEqType === COMP_EQ_SSMCS && !isStereoChannel(nodeId)) return null;
  const eqSec = channelSections(model, nodeId, compEqType).find((s) => s.key === "eqOn");
  return eqSec ? eqBandsFrom(eqSec.param, [eqSec.y]) : null;
}

/** Whether this node has a 4-band PEQ at all. Distinct from the CONSOLE's `hasEq`, which is
 *  also true for a mono channel in SSMCS mode — that EQ is the morphing strip's, not this
 *  one. `inputEq`/`outputEq` already answer per node kind; this is the pair of them. */
export function hasEq(model: DeviceModel, nodeId: string, compEqType: number): boolean {
  return (inputEq(model, nodeId, compEqType) ?? outputEq(nodeId)) !== null;
}

/** EQ 1-knob params for an EQ (input channel or output bus): ON / TYPE / LEVEL sit
 *  2 / 3 / 4 params after the EQ-ON anchor. */
export interface EqOneKnobControl {
  on: number;
  type: number;
  level: number;
  instances: number[];
}

function eqOneKnobFrom(eqOnParam: number, instances: number[]): EqOneKnobControl {
  const b = eqOnParam + 2;
  return { on: b, type: b + 1, level: b + 2, instances };
}

/** Resolve the EQ 1-knob for a node, or null if it has no EQ (e.g. a mono channel
 *  in SSMCS mode). Reuses the same EQ-ON anchor as inputEq/outputEq. */
export function eqOneKnob(model: DeviceModel, nodeId: string, compEqType: number): EqOneKnobControl | null {
  const out = busEqOn(nodeId);
  if (out) return eqOneKnobFrom(out.param, out.instances);
  if (compEqType === COMP_EQ_SSMCS && !isStereoChannel(nodeId)) return null;
  const eqSec = channelSections(model, nodeId, compEqType).find((s) => s.key === "eqOn");
  return eqSec ? eqOneKnobFrom(eqSec.param, [eqSec.y]) : null;
}

/** One slider value of a tuning screen's parameters: its catalog name + plan-domain
 *  range. GATE/COMP/DUCKER details and the 4-band PEQ's per-band values all take this
 *  shape, so the screens render them the same way. */
export interface DynField {
  /** The GateParams / CompParams / EqBand sub-field this controls, or one of the SSMCS
   *  keys the descriptor flattens its nested sub-objects onto. */
  key: keyof GateParams | keyof CompParams | keyof EqBand | SsmcsFieldKey | InsertFxFieldKey | FxFieldKey;
  /** The catalog row the numeric writer emits this value through. Absent for the SSMCS
   *  strip, whose commands are built from the plan's own nested shape
   *  (`pushSsmcsCommands`) rather than from a field table — a name here would be a
   *  second spelling of the same address that nothing reads and nothing checks. */
  name?: ParamName;
  min: number;
  max: number;
  step: number;
  /** Device default in plan units, shown before a fetch. */
  def: number;
  /** How the number reads. `raw` = a broker integer whose display goes through a
   *  device curve, so the surface printing it supplies the text; `formatDyn` prints
   *  the integer itself, which is right for the one SSMCS value that has no unit. A
   *  time names which of the unit's time readouts it takes (`TimeReadout`). `dbWhole` is a
   *  level on the 1 dB grid, printed in whole dB; `db` a finer one, printed to one decimal. */
  unit: "db" | "dbWhole" | TimeReadout | "ratio" | "hz" | "q" | "raw";
  /** Slider positions for a value the device sweeps logarithmically (an EQ band
   *  frequency spans three decades, which a linear slider cannot resolve at the
   *  bottom). Absent = the slider carries the value itself. */
  logSteps?: number;
  /** The values the control stops on, where they are neither a linear grid nor a
   *  logarithmic one — the compressor Ratio ladder, whose spacing widens in stages
   *  across its range, and the GATE / COMP / DUCKER time values. The position is an
   *  index into this table, and `step` (the table's narrowest spacing) does not reach
   *  the control. */
  steps?: readonly number[];
  /** The plan can hold this field's value between two of `steps`, so an arrow press or a
   *  wheel notch turns from the held value rather than from the position the slider shows
   *  (`stopStep`). */
  heldOffStops?: true;
  /** Step the device's push-and-turn fine mode uses for this value, when it has
   *  one. Confirmed for exactly one dynamics parameter (see the COMP gain row);
   *  every other GATE/COMP/DUCKER value is a measured negative, so this is not a
   *  class rule and must not be inferred from the unit. */
  fineStep?: number;
  /** How far one arrow press or wheel notch moves the value, where that is further than
   *  `step` — the unit's encoder click on a value held to a finer grid. The finer part is
   *  kept. Absent = the turn is one `step`. */
  turnStep?: number;
}

/** A field `pushDynCommands` can emit. The writer reads `name`, so requiring it here
 *  is what keeps a nameless field (the SSMCS ones) out of that path at compile time
 *  instead of through a runtime branch nothing could ever reach. */
export type EmittedDynField = DynField & { name: ParamName };

/** The SSMCS-only value keys, which no plan sub-object of its own is named after: the
 *  descriptor flattens `ssmcs` and `ssmcs.sc` onto one record, so the side-chain
 *  filter's three values are prefixed to keep them apart from the compressor's. */
export type SsmcsFieldKey = "compDrive" | "morphing" | "outGain" | "scQ" | "scFreq" | "scGain";

/** An insert-FX engine value, named by the FAMILY and the SLOT it occupies. Those values
 *  live in `insertFxParams` keyed the same way rather than in a sub-object of their own, so
 *  there is no parameter name to borrow — and the same slot means a different parameter
 *  under each family, which is why the family is part of the key rather than resolved from
 *  the plan when the value is written. A row can outlive the family it was built for: a
 *  device follow can replace the effect while a slider is under the pointer, and the drag
 *  goes on firing at the row that is already detached. With the family in the key, that
 *  write lands in the outgoing family's parked values — where a selector change would have
 *  parked them anyway — instead of under the incoming family's slot of the same number,
 *  which is a different parameter on a different scale. */
export type InsertFxFieldKey = `ifx:${string}:${number}`;

/** An FX-channel effect value, named by the PLAN KEY it is stored under.
 *
 *  Not the family and the slot an insert-FX value takes. The two delay types are both
 *  family `delay` and both put the delay time on slot 6, so that spelling would name two
 *  different parameters with one key — they accept different RANGES, and a Mono time handed
 *  to a Ping Pong descriptor is clamped at the unit while the plan goes on showing the value
 *  it was set to. The catalogue's own keys already carry a family name exactly where two
 *  families collide (`revxHpf` / `revr3Hpf` / `delayHpf`, `delay` / `pingPongDelay`) and
 *  share one where the parameter really is one (`reverbTime` is slot 7 of both reverbs), so
 *  they are unique and they mean the right thing. The keys are the params map's, which is
 *  every value the FX EFFECT screen edits.
 *
 *  The purpose is `InsertFxFieldKey`'s: a device follow can replace the effect while a knob
 *  is under the pointer, and the drag goes on firing at a row that is already detached —
 *  keyed this way that write lands under the outgoing family's own name instead of under
 *  the incoming family's parameter of the same slot. */
export type FxFieldKey = `fx:${string}`;

const SSMCS_SC_PREFIX = /^sc[A-Z]/;

/** Whether a flattened SSMCS key belongs to the side-chain filter rather than the
 *  compressor — which is what decides the sub-object a read and a write go to. */
export const isSsmcsScKey = (key: string): boolean => key === "scOn" || SSMCS_SC_PREFIX.test(key);

/**
 * The plan key a flattened SSMCS key is stored under.
 *
 * Only the screen sees the prefixed spelling; the plan, the writer and a control id all
 * know these as `on` / `q` / `freq` / `gain` under `sc`. Stating the translation once is
 * what this exists for — spelled out per call site, the MIDI catalog translated it for
 * the control ID and not for the plan key, so a mapped side-chain Q wrote `ssmcs.sc.scQ`.
 * Nothing reads that key: the value never reached the wire, the screen never moved, and
 * `get` read the same private key back, so the controller's feedback confirmed it.
 */
export function ssmcsPlanKey(key: string): string {
  if (key === "scOn") return "on";
  return SSMCS_SC_PREFIX.test(key) ? key[2].toLowerCase() + key.slice(3) : key;
}

// GATE detail (29-33) and COMP detail (35-40, the COMP->EQ comp bank). Ranges and
// defaults in plan units (dB / ms / N:1); the broker bounds come from the encoders.
// The COMP knee (37) is a separate enum dropdown, not a slider, so it is not here.
// Every time value offers the stops the unit's own control stops on (dyn-time-stops.ts):
// one attack table for GATE, COMP and DUCKER, and one for GATE decay and COMP release.
const GATE_FIELDS: EmittedDynField[] = [
  { key: "threshold", name: "GATE_THRESHOLD", min: -72, max: 0, step: 1, def: -50, unit: "dbWhole" },
  { key: "range", name: "GATE_RANGE", min: GATE_RANGE_OFF_DB, max: 0, step: 1, def: -56, unit: "dbWhole" },
  {
    key: "attack",
    name: "GATE_ATTACK",
    min: DYN_ATTACK_MIN_MS,
    max: DYN_ATTACK_MAX_MS,
    step: DYN_ATTACK_FINEST_MS,
    def: 20.17,
    unit: "attack",
    steps: DYN_ATTACK_STOPS_MS,
  },
  {
    key: "hold",
    name: "GATE_HOLD",
    min: DYN_HOLD_MIN_MS,
    max: DYN_HOLD_MAX_MS,
    step: DYN_HOLD_FINEST_MS,
    def: 15.3,
    unit: "hold",
    steps: DYN_HOLD_STOPS_MS,
  },
  {
    key: "decay",
    name: "GATE_DECAY",
    min: DYN_RELEASE_MIN_MS,
    max: DYN_RELEASE_MAX_MS,
    step: DYN_RELEASE_FINEST_MS,
    def: 150.2,
    unit: "release",
    steps: DYN_RELEASE_STOPS_MS,
  },
];
// The channel COMP's Ratio (`36`) offers the stops the unit's own control stops on, ending at
// the one it writes INF:1 for. The finest spacing on the ladder is 0.05, below 4.00:1.
const COMP_FIELDS: EmittedDynField[] = [
  { key: "threshold", name: "COMP_THRESHOLD", min: -54, max: 0, step: 1, def: -18, unit: "dbWhole" },
  {
    key: "ratio",
    name: "COMP_RATIO",
    min: DYN_RATIO_MIN,
    max: COMP_RATIO_INF,
    step: COMP_RATIO_FINEST_STEP,
    def: 3,
    unit: "ratio",
    steps: COMP_RATIO_CH_STEPS,
  },
  { key: "gain", name: "COMP_GAIN", min: 0, max: 18, step: 0.5, def: 2, unit: "db", fineStep: 0.1 },
  {
    key: "attack",
    name: "COMP_ATTACK",
    min: DYN_ATTACK_MIN_MS,
    max: DYN_ATTACK_MAX_MS,
    step: DYN_ATTACK_FINEST_MS,
    def: 34.58,
    unit: "attack",
    steps: DYN_ATTACK_STOPS_MS,
  },
  {
    key: "release",
    name: "COMP_RELEASE",
    min: DYN_RELEASE_MIN_MS,
    max: DYN_RELEASE_MAX_MS,
    step: DYN_RELEASE_FINEST_MS,
    def: 218,
    unit: "release",
    steps: DYN_RELEASE_STOPS_MS,
  },
];
// Ducker detail (260-263, stereo channel sidechain). Same shapes as GATE but no
// hold; decay shares the ×10 release scale with a wider range. Ordered as the
/**
 * One band of the 4-band PEQ, in the order the unit's own screen reads it (Q, Freq, Gain).
 * The set depends on the filter type only in which of them the device reads — a pass filter
 * reads neither Q nor gain (measured: Q 0.71 and Q 4.00 draw an identical high-pass) and a
 * shelf reads no Q — so every band offers all three and the screen locks the rest. The gain
 * carries the 0.1 dB push-and-turn fine grid, one of the two values on the unit confirmed to
 * have one.
 */
export function eqBandFields(index: number): EmittedDynField[] {
  return [
    { key: "q", name: "EQ_BAND_Q", min: EQ_Q_MIN, max: EQ_Q_MAX, step: 0.1, def: EQ_Q_DEFAULT, unit: "q" },
    {
      key: "freq",
      name: "EQ_BAND_FREQ",
      min: EQ_FREQ_MIN_HZ,
      max: EQ_FREQ_MAX_HZ,
      step: 1,
      def: EQ_BAND_DEFAULT_FREQ_HZ[index],
      unit: "hz",
      // Three decades: a linear slider resolves nothing at the bottom of them.
      logSteps: 1000,
    },
    {
      key: "gain",
      name: "EQ_BAND_GAIN",
      min: EQ_GAIN_MIN_DB,
      max: EQ_GAIN_MAX_DB,
      step: 0.5,
      def: 0,
      unit: "db",
      fineStep: 0.1,
    },
  ];
}

/**
 * The SSMCS strip's slider fields, in the order the unit's own screens read them.
 *
 * Every value is the RAW broker integer, which is also the device's own grid — one
 * slider step is one detent, so none of these needs `logSteps` even where the device
 * curve is logarithmic. What that costs is the display: a raw number says nothing, so
 * the screen supplies the text through `fieldText` and the unit is `raw`.
 *
 * `SSMCS_INITIAL` is the factory capture, so a field's default and a fresh channel's
 * value are one fact rather than two. That is also why these are functions rather than
 * constants: `plan.ts` reaches this module through `constraints.ts`, so a constant here
 * would be built while `SSMCS_INITIAL` is still undefined.
 */
export const ssmcsMainFields = (): DynField[] => [
  {
    key: "compDrive",
    min: SSMCS_COMP_DRIVE_MIN,
    max: SSMCS_COMP_DRIVE_MAX,
    step: 1,
    def: SSMCS_INITIAL.compDrive,
    unit: "raw",
  },
  {
    key: "morphing",
    min: SSMCS_MORPHING_MIN,
    max: SSMCS_MORPHING_MAX,
    step: 1,
    def: SSMCS_INITIAL.morphing,
    unit: "raw",
  },
  { key: "outGain", min: SSMCS_GAIN_MIN, max: SSMCS_GAIN_MAX, step: 1, def: SSMCS_INITIAL.outGain, unit: "raw" },
];

export const ssmcsCompFields = (): DynField[] => [
  {
    key: "attack",
    min: SSMCS_ATTACK_RAW_MIN,
    max: SSMCS_ATTACK_RAW_MAX,
    step: 1,
    def: SSMCS_INITIAL.comp.attack,
    unit: "raw",
  },
  {
    key: "release",
    min: SSMCS_RELEASE_RAW_MIN,
    max: SSMCS_RELEASE_RAW_MAX,
    step: 1,
    def: SSMCS_INITIAL.comp.release,
    unit: "raw",
  },
  {
    key: "ratio",
    min: SSMCS_RATIO_RAW_MIN,
    max: SSMCS_RATIO_RAW_MAX,
    step: 1,
    def: SSMCS_INITIAL.comp.ratio,
    unit: "raw",
  },
  { key: "scQ", min: SSMCS_Q_RAW_MIN, max: SSMCS_Q_RAW_MAX, step: 1, def: SSMCS_INITIAL.sc.q, unit: "raw" },
  { key: "scFreq", min: SSMCS_FREQ_RAW_MIN, max: SSMCS_FREQ_RAW_MAX, step: 1, def: SSMCS_INITIAL.sc.freq, unit: "raw" },
  { key: "scGain", min: SSMCS_GAIN_MIN, max: SSMCS_GAIN_MAX, step: 1, def: SSMCS_INITIAL.sc.gain, unit: "raw" },
];

/** The three SSMCS EQ bands, in the order the screen offers them. */
export const SSMCS_EQ_BAND_NAMES = ["low", "mid", "high"] as const;
export type SsmcsEqBandName = (typeof SSMCS_EQ_BAND_NAMES)[number];

/** Whether a band reads a Q at all: MID is peaking, LOW and HIGH are shelves with no Q
 *  parameter on the device. The Q row is still offered on all three (locked on the
 *  shelves), so the panel keeps one height across the three bands. */
export const ssmcsEqBandHasQ = (band: SsmcsEqBandName): boolean => band === "mid";

/** One SSMCS EQ band's fields. The frequency range is the band's own — LOW stops at
 *  1 kHz and HIGH starts near 500 Hz — which is why this is a function of the band. */
export function ssmcsEqBandFields(band: SsmcsEqBandName): DynField[] {
  const init = SSMCS_INITIAL.eq[band];
  return [
    { key: "q", min: SSMCS_Q_RAW_MIN, max: SSMCS_Q_RAW_MAX, step: 1, def: SSMCS_INITIAL.eq.mid.q, unit: "raw" },
    {
      key: "freq",
      min: band === "high" ? SSMCS_EQ_HIGH_FREQ_RAW_MIN : SSMCS_FREQ_RAW_MIN,
      max: band === "low" ? SSMCS_EQ_LOW_FREQ_RAW_MAX : SSMCS_FREQ_RAW_MAX,
      step: 1,
      def: init.freq,
      unit: "raw",
    },
    { key: "gain", min: SSMCS_GAIN_MIN, max: SSMCS_GAIN_MAX, step: 1, def: init.gain, unit: "raw" },
  ];
}

// device DUCKER screen reads them (Range / Attack / Decay graph handles, then the
// Threshold box); each field carries its own param name, so order is display-only.
export const DUCKER_FIELDS: EmittedDynField[] = [
  { key: "range", name: "DUCKER_RANGE", min: -70, max: 0, step: 1, def: -56, unit: "dbWhole" },
  {
    key: "attack",
    name: "DUCKER_ATTACK",
    min: DYN_ATTACK_MIN_MS,
    max: DYN_ATTACK_MAX_MS,
    step: DYN_ATTACK_FINEST_MS,
    def: 20.17,
    unit: "attack",
    steps: DYN_ATTACK_STOPS_MS,
  },
  {
    key: "decay",
    name: "DUCKER_DECAY",
    min: DUCKER_DECAY_MIN_MS,
    max: DUCKER_DECAY_MAX_MS,
    step: DUCKER_DECAY_FINEST_MS,
    def: 1000,
    unit: "duckerDecay",
    steps: DUCKER_DECAY_STOPS_MS,
  },
  { key: "threshold", name: "DUCKER_THRESHOLD", min: -60, max: 0, step: 1, def: -40, unit: "dbWhole" },
];

/**
 * Value → slider position, and back. Identity for a linear field; for a logarithmic
 * one (an EQ band frequency spans three decades, which a linear slider cannot resolve
 * at the bottom) the position is an index into `logSteps` even divisions of the log
 * range, and for a field with a stop table it is an index into that table. It lives
 * beside the field table rather than in the screen that draws the slider, because the
 * MIDI catalog has to land on the same values. What shares these two is every field
 * that carries POSITIONS: its codec resolves through them, so a normalized MIDI value
 * and a dragged slider cannot drift on one. A linear field's codec is `linearCodec` on
 * the same min / max / step — it agrees at every position the grid holds and bounds the
 * top itself, which channel-tuning.md "MIDI assignment" states.
 */
export function dynToPos(f: DynField, v: number): number {
  if (f.steps) return nearestStepIndex(f.steps, v);
  if (f.logSteps === undefined) return v;
  return Math.round((f.logSteps * Math.log(v / f.min)) / Math.log(f.max / f.min));
}
export function dynFromPos(f: DynField, pos: number): number {
  // A position off the table, or one that is not a number, reads as an end of it: an
  // index outside it answers `undefined`, which is not a value any of this may carry.
  if (f.steps) return f.steps[Number.isFinite(pos) ? Math.min(f.steps.length - 1, Math.max(0, Math.round(pos))) : 0];
  if (f.logSteps === undefined) return pos;
  return Math.round(f.min * Math.exp((Math.log(f.max / f.min) * pos) / f.logSteps));
}

/** The range a field's slider runs over: the value itself where the position IS the
 *  value, and indices where it is not. The one place that decides it, so the two screen
 *  layouts and the MIDI codec cannot spell it three ways. */
export function dynPosRange(f: DynField): { min: number; max: number; step: number } {
  if (f.steps) return { min: 0, max: f.steps.length - 1, step: 1 };
  if (f.logSteps !== undefined) return { min: 0, max: f.logSteps, step: 1 };
  return { min: f.min, max: f.max, step: f.step };
}

/** Format one dynamics value for display, by its unit. The single source for the
 *  inspector, the gate screen and anything else that prints a DynField. */
export function formatDyn(v: number, unit: DynField["unit"]): string {
  if (unit === "raw") return String(v);
  // A level prints with no sign on a positive value; one held between grid steps prints its
  // whole-dB part, toward zero.
  if (unit === "dbWhole") return `${Math.trunc(v)} dB`;
  if (unit === "db") return `${v.toFixed(1)} dB`;
  if (unit === "ratio") return formatCompRatio(v, "comp");
  if (unit === "hz") return formatHz(v);
  if (unit === "q") return v.toFixed(2);
  return formatTime(v, unit);
}

/** Which of the unit's time readouts a value takes. Each prints in the decimals the unit's
 *  own screen gives that control, and every stop of the time tables prints as itself:
 *  `attack` (GATE / COMP / DUCKER / SSMCS Attack) three decimals below 10 ms and two from
 *  there; `release` (COMP / SSMCS Release, GATE Decay) one decimal; `hold` (GATE Hold) two
 *  decimals below 10 ms, one below 1 s and seconds with two from there; `duckerDecay` one
 *  decimal, in seconds from 1 s. */
export const TIME_READOUTS = ["attack", "release", "hold", "duckerDecay"] as const;
export type TimeReadout = (typeof TIME_READOUTS)[number];

/** A time in ms, as the unit prints it for that readout. */
export function formatTime(ms: number, readout: TimeReadout): string {
  switch (readout) {
    case "attack":
      return `${ms.toFixed(ms < 10 ? 3 : 2)} ms`;
    case "release":
      return `${ms.toFixed(1)} ms`;
    case "hold":
      return ms < 1000 ? `${ms.toFixed(ms < 10 ? 2 : 1)} ms` : `${(ms / 1000).toFixed(2)} s`;
    case "duckerDecay":
      return ms < 1000 ? `${ms.toFixed(1)} ms` : `${(ms / 1000).toFixed(1)} s`;
  }
}

/** The display text for a field's current value, including GATE range's -∞ notch
 *  (one step below its -72 dB floor = fully closed). Keyed off the field itself so
 *  the two surfaces that print it cannot disagree on which field owns the notch —
 *  they previously tested `name === "GATE_RANGE"` and `key === "range"`. */
export function dynValueText(f: DynField, v: number): string {
  if (f.name === "GATE_RANGE" && v <= GATE_RANGE_OFF_DB) return "-∞ dB";
  return formatDyn(v, f.unit);
}

/** GATE/COMP detail controls for a channel: the slider fields and the instance index. */
export interface ChannelDynamics {
  y: number;
  gate: EmittedDynField[];
  /** COMP slider fields, or null in SSMCS mode (the morphing strip replaces COMP). */
  comp: EmittedDynField[] | null;
}

/**
 * Resolve a channel's GATE/COMP detail controls, or null if it has none. GATE and
 * COMP are MONO IN-channel features (user guide); COMP additionally exists only in
 * COMP->EQ mode (SSMCS replaces it with the morphing strip). Confirmed on a mono
 * channel by live scan (research §12.26).
 */
export function channelDynamics(model: DeviceModel, nodeId: string, compEqType: number): ChannelDynamics | null {
  const cc = channelControl(model, nodeId);
  if (!cc || !cc.hasMicStrip) return null;
  return { y: cc.y, gate: GATE_FIELDS, comp: compEqType === COMP_EQ_SSMCS ? null : COMP_FIELDS };
}

// Push the value-set commands for a GATE/COMP detail section the plan has set.
// Each value is clamped to its DynField plan-domain range before encoding, since
// the shared encoders (e.g. centiDbToVd / releaseToVd) only clamp to the broker's
// raw int/scale bounds, not the per-field dB/ms limits the UI enforces.
function pushDynCommands(
  out: VdCommand[],
  fields: EmittedDynField[],
  y: number,
  vals: Record<string, number | undefined>,
): void {
  for (const f of fields) {
    const v = vals[f.key];
    if (v === undefined) continue;
    // A field with a stop table sends a stop. The control offers nothing else, but a plan
    // saved before it had one — or hand-edited — can hold a value between two, and the unit
    // has no setting there.
    out.push(command(f.name, y, admitLeaf(dynRule(f), v)));
  }
}

/**
 * What one stored node-param leaf can hold, as the write sends it: a window, an INTEGER window
 * (a broker raw, rounded first), a window whose value then moves to the nearest of a stop
 * table, or a MENU, where a value off it is sent as the menu's default. The emit bounds each
 * leaf through the rule here (`admitLeaf`), and the load bounds a document to the same answer
 * (`plan-validate.ts` `paramRangeProblems`), so the plan, the screen and the wire read one
 * value.
 */
export type BoundRule =
  | { min: number; max: number; integer?: true; steps?: readonly number[]; grid?: number }
  | { menu: readonly number[]; def: number; map?: Readonly<Record<number, number>> };

/** A bound rule, or a leaf the write never sends at all (a filter type on a fixed-peaking band),
 *  which the load removes. */
export type LeafRule = BoundRule | { unsent: true };

/** The value a rule admits for `v`, which is the value the write sends for it. */
export function admitLeaf(rule: BoundRule, v: number): number {
  if ("menu" in rule) return rule.menu.includes(v) ? v : (rule.map?.[v] ?? rule.def);
  const x = rule.integer ? Math.round(v) : v;
  const bounded = x < rule.min ? rule.min : x > rule.max ? rule.max : x;
  if (rule.grid !== undefined) return rule.min + rule.grid * Math.round((bounded - rule.min) / rule.grid);
  return rule.steps ? rule.steps[nearestStepIndex(rule.steps, bounded)] : bounded;
}

const menuRule = (options: readonly { value: number }[], def: number): BoundRule => ({
  menu: options.map((o) => o.value),
  def,
});

/** A channel's Rec Point as the write sends it: a stage the channel offers stands; in SSMCS mode
 *  a PRE EQ is PRE COMP, the move the unit makes itself on that switch; anything else is PRE
 *  FADER. */
export function recPointRule(stereo: boolean, ssmcs: boolean): BoundRule {
  return {
    ...menuRule(recPointOptionsFor(stereo, ssmcs), REC_POINT_DEFAULT),
    ...(ssmcs ? { map: { [REC_POINT_PRE_EQ]: REC_POINT_PRE_COMP } } : {}),
  };
}

const dynRule = (f: DynField): BoundRule => ({ min: f.min, max: f.max, ...(f.steps ? { steps: f.steps } : {}) });
const rawRule = (min: number, max: number): BoundRule => ({ min, max, integer: true });

/** An insert-FX engine key's family and slot: the family its own key names, or — for a bare
 *  slot number — the family the node's selector names. Null for a key that is neither. */
function insertFxKeySlot(
  key: string,
  selected: import("./insert-fx-effect").InsertFxFamily | null,
): { family: import("./insert-fx-effect").InsertFxFamily; slot: number } | null {
  const qualified = /^(.+):([0-9]+)$/.exec(key);
  if (qualified) {
    // Every family, from the catalogue's own selectors.
    const family = [...INSERT_FX_OPTIONS, ...OUTPUT_INSERT_FX_OPTIONS]
      .map((o) => insertFxFamilyOf(o.value))
      .find((f) => f === qualified[1]);
    return family ? { family, slot: Number(qualified[2]) } : null;
  }
  return /^[0-9]+$/.test(key) && selected ? { family: selected, slot: Number(key) } : null;
}

/**
 * Every leaf of one node's params that the write bounds, by dotted path, with the rule it is
 * bounded by. `np` is the node as stored; the insert-FX engine keys come from it, since the map
 * carries one namespace per family.
 */
export function nodeLeafRules(model: DeviceModel, nodeId: string, np: NodeParams | undefined): [string, LeafRule][] {
  const out: [string, LeafRule][] = [];
  const cc = channelControl(model, nodeId);
  if (cc) {
    const compEqType = admitLeaf(menuRule(COMP_EQ_OPTIONS, COMP_EQ_COMP_FIRST), np?.compEqType ?? COMP_EQ_COMP_FIRST);
    out.push(["recPoint", recPointRule(!cc.hasMicStrip, cc.hasMicStrip && compEqType === COMP_EQ_SSMCS)]);
  }
  // A MIC/LINE node's A.Gain per jack, to its jack's range; a digital source's D.Gain.
  for (const side of ["L", "R"] as const) {
    const gain = jackGainRange(model.id, { node: nodeId, side }, np);
    if (gain) out.push([`gain${side}`, { min: gain.minDb, max: gain.maxDb }]);
  }
  if (D_GAIN_SOURCES[nodeId] && model.nodes.some((n) => n.id === nodeId))
    out.push(["dGain", { min: D_GAIN_MIN_DB, max: D_GAIN_MAX_DB }]);
  if (cc?.hasHpf) out.push(["hpfFreq", { min: HPF_FREQ_MIN_HZ, max: HPF_FREQ_MAX_HZ, grid: HPF_FREQ_STEP_HZ }]);
  // PAN/BAL exists only while the pair is linked; an unlinked pair reads PAN.
  if (model.channelPairs.some(([primary]) => primary === nodeId))
    out.push([
      "panBal",
      np?.stereoLink === true ? menuRule(PAN_BAL_OPTIONS, PAN_BAL_PAN) : { menu: [PAN_BAL_PAN], def: PAN_BAL_PAN },
    ]);
  if (cc?.hasMicStrip) {
    out.push(["compEqType", menuRule(COMP_EQ_OPTIONS, COMP_EQ_COMP_FIRST)]);
    for (const f of GATE_FIELDS) out.push([`gate.${f.key}`, dynRule(f)]);
    for (const f of COMP_FIELDS) out.push([`comp.${f.key}`, dynRule(f)]);
    out.push(["comp.knee", menuRule(COMP_KNEE_OPTIONS, COMP_KNEE_DEFAULT)]);
    out.push(["comp.oneKnobLevel", rawRule(0, 100)]);
    out.push(["ssmcs.comp.knee", menuRule(COMP_KNEE_OPTIONS, COMP_KNEE_DEFAULT)]);
    for (const f of ssmcsMainFields()) out.push([`ssmcs.${f.key}`, rawRule(f.min, f.max)]);
    for (const f of ssmcsCompFields()) {
      const group = isSsmcsScKey(f.key) ? "sc" : "comp";
      out.push([`ssmcs.${group}.${ssmcsPlanKey(f.key)}`, rawRule(f.min, f.max)]);
    }
    out.push(["ssmcs.comp.threshold", rawRule(SSMCS_COMP_INTERNAL_MIN, SSMCS_COMP_INTERNAL_MAX)]);
    out.push(["ssmcs.comp.makeup", rawRule(SSMCS_COMP_INTERNAL_MIN, SSMCS_COMP_INTERNAL_MAX)]);
    for (const band of SSMCS_EQ_BAND_NAMES) {
      for (const f of ssmcsEqBandFields(band)) {
        if (f.key === "q" && !ssmcsEqBandHasQ(band)) continue;
        out.push([`ssmcs.eq.${band}.${f.key}`, rawRule(f.min, f.max)]);
      }
    }
  }
  const peq = inputEq(model, nodeId, COMP_EQ_COMP_FIRST) ?? outputEq(nodeId);
  for (const band of peq?.bands ?? []) {
    const at = `eqBands.${band.index}`;
    if (band.type === null) out.push([`${at}.type`, { unsent: true }]);
    else
      out.push([
        `${at}.type`,
        menuRule(band.name === "low" ? EQ_TYPE_LOW_OPTIONS : EQ_TYPE_HIGH_OPTIONS, EQ_TYPE_SHELVING),
      ]);
    for (const f of eqBandFields(band.index)) out.push([`${at}.${f.key}`, dynRule(f)]);
  }
  if (eqOneKnob(model, nodeId, COMP_EQ_COMP_FIRST)) {
    out.push(["eqOneKnob.type", menuRule(EQ_ONE_KNOB_TYPE_ALL_OPTIONS, EQ_ONE_KNOB_TYPE_DEFAULT)]);
    out.push(["eqOneKnob.level", rawRule(EQ_ONE_KNOB_LEVEL_MIN, EQ_ONE_KNOB_LEVEL_MAX)]);
  }
  if (MIX_FADER_INSTANCES[nodeId]) out.push(["busType", menuRule(BUS_TYPE_OPTIONS, BUS_TYPE_VARI)]);
  if (duckerControl(model, nodeId)) for (const f of DUCKER_FIELDS) out.push([`ducker.${f.key}`, dynRule(f)]);
  if (nodeId === "bus.osc") {
    out.push(["osc.level", { min: OSC_LEVEL_MIN_DB, max: OSC_LEVEL_MAX_DB }]);
    out.push(["osc.mode", menuRule(OSC_MODE_OPTIONS, OSC_MODE_SINE)]);
    out.push(["osc.interval", rawRule(1, 30)]);
  }
  if (nodeId === "bus.stream")
    out.push(["delay.frameRate", menuRule(DELAY_FRAME_RATE_OPTIONS, DELAY_FRAME_RATE_DEFAULT)]);
  const ifx = insertFxControl(model, nodeId);
  const params = np?.insertFxParams;
  if (ifx && params && typeof params === "object") {
    const sel = np?.insertFx;
    const selected = typeof sel === "number" && ifx.options.some((o) => o.value === sel) ? insertFxFamilyOf(sel) : null;
    for (const key of Object.keys(params)) {
      const at = insertFxKeySlot(key, selected);
      const spec = at && insertFxWritableSlots(at.family).find((sp) => sp.slot === at.slot);
      if (spec) out.push([`insertFxParams.${key}`, rawRule(spec.rawMin, spec.rawMax)]);
    }
  }
  return out;
}

// Push the SSMCS detail value-set commands for one MONO IN channel. Values are
// raw broker integers; the inspector formats them. Only fields the plan set are
// emitted (readback populates them all, keeping emit∘readback a fixed point).
function pushSsmcsBand(
  out: VdCommand[],
  y: number,
  b: SsmcsBand | undefined,
  onName: ParamName,
  qName: ParamName | null,
  freqName: ParamName,
  gainName: ParamName,
  freqMin: number,
  freqMax: number,
): void {
  if (!b) return;
  if (b.on !== undefined) out.push(command(onName, y, b.on ? 1 : 0));
  if (qName && isRaw(b.q)) out.push(command(qName, y, boundRaw(b.q, SSMCS_Q_RAW_MIN, SSMCS_Q_RAW_MAX)));
  if (isRaw(b.freq)) out.push(command(freqName, y, boundRaw(b.freq, freqMin, freqMax)));
  if (isRaw(b.gain)) out.push(command(gainName, y, boundRaw(b.gain, SSMCS_GAIN_MIN, SSMCS_GAIN_MAX)));
}

// FX-channel effect: the EFFECT TYPE selector (679/683 at y0) plus the effect
// parameter array (681/685, addressed by slot). Emitted as absolute state for the
// effect's family; raw values pass straight through (the plan stores raw). The
// type is a sideEffect (writing it repopulates the array on the device), so live
// converges + re-reads. fxIndex = 0 (FX1) / 1 (FX2).

/** Whether a plan value can go out as a raw at all: a finite number. Anything else is not
 *  sent, since the shell refuses a write whose value is not an integer. */
function isRaw(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

// A plan-sourced raw with a catalog default: a non-finite value takes the default
// (plan.ts strips those on load, so this covers hand-built plans only).
function planRaw(v: number | undefined, def: number): number {
  return Number.isFinite(v) ? (v as number) : def;
}

// Coerce an enum to its menu — the same firewall as boundRaw, for the other
// passthrough encoding. An off-menu value would otherwise be written verbatim and
// select something the plan never named.
function boundEnum(v: number, options: readonly { value: number }[], def: number): number {
  return admitLeaf(menuRule(options, def), v);
}

function pushFxEffectCommands(
  out: VdCommand[],
  fxIndex: number,
  fx: FxEffectParams,
  includeDeviceDriven: boolean,
): void {
  const typeId = FX_EFFECT_TYPE_PARAM[fxIndex];
  const arrId = FX_EFFECT_ARRAY_PARAM[fxIndex];
  // A type this CHANNEL's menu does not offer (hand-edited / ?plan= payload) falls
  // back to the channel's factory type rather than being written verbatim: the
  // device would take an unknown selector value, and fxFamilyOf would emit
  // delay-family slots with it.
  const type = resolveFxEffectType(fxIndex, fx.type);
  out.push(rawCommand("FX_EFFECT_TYPE", typeId, "enum", 0, type));
  // Slots 1 and 2 are NOT sent: the array carries only the selected type's descriptor slots,
  // and the unit keeps whatever it holds there.
  // The slot the unit is computing for itself, skipped for the reason the insert-FX loop
  // below skips its own: while tempo Sync is on the unit derives the delay time from the BPM
  // and the note value, and it ACCEPTS a write to that slot and holds it — so re-sending the
  // plan's copy does not merely repeat what is there, it takes the delay time off the note
  // value and leaves Sync on with nothing driving it.
  const driven = includeDeviceDriven ? new Set<string>() : new Set(fxRowOwners(type, fx.params).keys());
  for (const desc of fxParams(type)) {
    if (driven.has(desc.key)) continue;
    const raw = fxDescRawToSend(desc, fx.params?.[desc.key]);
    out.push(rawCommand("FX_EFFECT_PARAM", arrId, "raw", desc.slot, raw));
  }
}

// Insert-FX effect: the engine parameter array for the selected effect family
// (guitar 697 / pitch 701 / compander 689 / output 693). Slots + defaults from
// the calibrated catalog; mirror slots (Pitch Coarse/Fine/Formant) get the same
// raw. The selector (emitted by the caller) binds the engine first.
// Only slots the plan carries are written. A selection and a load put every writable
// slot of the selected type in the plan at that type's own default
// (`seedInsertFxParams`), so the plan holds what the screen shows. The slots
// iterated here are the ones a readback fills too: insertFxReadableSlots returns
// this list, and no slot is read-only. What a read fills and this does not send is
// the driven set skipped below, so a slot to stop writing belongs in that set
// rather than out of the writable list, which would stop the read as well.
// A slot is read under the selected family's own key, then under the bare slot
// number — the device-shaped namespace a readback writes, which is by construction
// the family the selector named at the time. Anything the plan stored for ANOTHER
// family stays behind its own key and is never emitted here: those raws mean a
// different parameter under a different law.
/** No slot is the device's — every family but the two that have a control handing values
 *  over, and those two with that control off. Shared rather than built per call: this runs
 *  per node on every live-sync flush. */
const NOTHING_DRIVEN: ReadonlySet<number> = new Set();

function pushInsertFxEffectCommands(
  out: VdCommand[],
  engine: number,
  family: import("./insert-fx-effect").InsertFxFamily,
  params: Record<string, number> | undefined,
  includeDeviceDriven: boolean,
): void {
  if (!params) return;
  // The values a control on this effect has handed to the unit, skipped for the same reason
  // the COMP 1-knob's set is below: the multi-band compressor's Level recomputes three
  // values per band while it is on, and Pitch Fix's MIDI Control clears the note mask and
  // the Scale when it is switched on. Emitting either is not merely redundant — anything
  // re-sending the plan's copy after the unit has acted puts the pre-change values back on
  // it, which is what a converge sharing the flush does.
  const driven = includeDeviceDriven ? NOTHING_DRIVEN : insertFxDeviceDriven(family, params);
  // …and the slots that DRIVE those go out under a name of their own, because writing one
  // makes the unit recompute what the loop above is skipping. The name is what carries the
  // read-back (params.ts, INSERT_FX_DRIVER); sent as INSERT_FX_EFFECT they are ordinary
  // values, nothing reads the unit's own result, and the plan's stale copy of it is what
  // the next flush sends once the control is switched off.
  const drivers = insertFxDriverSlots(family);
  for (const s of insertFxWritableSlots(family)) {
    if (driven.has(s.slot)) continue;
    // A slot the plan does not hold as a finite raw sends nothing; no default is
    // substituted here.
    const raw = insertFxSlotRaw(params[insertFxParamKey(family, s.slot)] ?? params[String(s.slot)], s);
    if (raw === undefined) continue;
    const name = drivers.has(s.slot) ? "INSERT_FX_DRIVER" : "INSERT_FX_EFFECT";
    out.push(rawCommand(name, engine, "raw", s.slot, raw));
    if (s.mirror !== undefined) out.push(rawCommand(name, engine, "raw", s.mirror, raw));
  }
}

function pushSsmcsCommands(out: VdCommand[], y: number, s: SsmcsParams | undefined): void {
  if (!s) return;
  // Every continuous value is a passthrough "raw" (or the knee "enum"), so it
  // carries the same firewall as the FX / DynFields paths: a hand-edited / ?plan=
  // raw is bounded to the calibrated vd.ts range before it reaches the device.
  if (s.on !== undefined) out.push(command("SSMCS_ON", y, s.on ? 1 : 0));
  if (isRaw(s.compDrive))
    out.push(command("SSMCS_COMP_DRIVE", y, boundRaw(s.compDrive, SSMCS_COMP_DRIVE_MIN, SSMCS_COMP_DRIVE_MAX)));
  if (isRaw(s.morphing))
    out.push(command("SSMCS_MORPHING", y, boundRaw(s.morphing, SSMCS_MORPHING_MIN, SSMCS_MORPHING_MAX)));
  if (isRaw(s.outGain)) out.push(command("SSMCS_OUT_GAIN", y, boundRaw(s.outGain, SSMCS_GAIN_MIN, SSMCS_GAIN_MAX)));
  const c = s.comp;
  if (c) {
    if (isRaw(c.attack))
      out.push(command("SSMCS_COMP_ATTACK", y, boundRaw(c.attack, SSMCS_ATTACK_RAW_MIN, SSMCS_ATTACK_RAW_MAX)));
    if (isRaw(c.release))
      out.push(command("SSMCS_COMP_RELEASE", y, boundRaw(c.release, SSMCS_RELEASE_RAW_MIN, SSMCS_RELEASE_RAW_MAX)));
    if (isRaw(c.ratio))
      out.push(command("SSMCS_COMP_RATIO", y, boundRaw(c.ratio, SSMCS_RATIO_RAW_MIN, SSMCS_RATIO_RAW_MAX)));
    if (c.knee !== undefined)
      out.push(command("SSMCS_COMP_KNEE", y, boundEnum(c.knee, COMP_KNEE_OPTIONS, COMP_KNEE_DEFAULT)));
    if (isRaw(c.threshold))
      out.push(
        command("SSMCS_COMP_THRESHOLD", y, boundRaw(c.threshold, SSMCS_COMP_INTERNAL_MIN, SSMCS_COMP_INTERNAL_MAX)),
      );
    if (isRaw(c.makeup))
      out.push(command("SSMCS_COMP_MAKEUP", y, boundRaw(c.makeup, SSMCS_COMP_INTERNAL_MIN, SSMCS_COMP_INTERNAL_MAX)));
  }
  const sc = s.sc;
  if (sc) {
    if (sc.on !== undefined) out.push(command("SSMCS_SC_ON", y, sc.on ? 1 : 0));
    if (isRaw(sc.q)) out.push(command("SSMCS_SC_Q", y, boundRaw(sc.q, SSMCS_Q_RAW_MIN, SSMCS_Q_RAW_MAX)));
    if (isRaw(sc.freq))
      out.push(command("SSMCS_SC_FREQ", y, boundRaw(sc.freq, SSMCS_FREQ_RAW_MIN, SSMCS_FREQ_RAW_MAX)));
    if (isRaw(sc.gain)) out.push(command("SSMCS_SC_GAIN", y, boundRaw(sc.gain, SSMCS_GAIN_MIN, SSMCS_GAIN_MAX)));
  }
  const eq = s.eq;
  if (eq) {
    // Per-band freq sub-ranges: Low caps low, High floors high, Mid spans all.
    pushSsmcsBand(
      out,
      y,
      eq.low,
      "SSMCS_EQ_LOW_ON",
      null,
      "SSMCS_EQ_LOW_FREQ",
      "SSMCS_EQ_LOW_GAIN",
      SSMCS_FREQ_RAW_MIN,
      SSMCS_EQ_LOW_FREQ_RAW_MAX,
    );
    pushSsmcsBand(
      out,
      y,
      eq.mid,
      "SSMCS_EQ_MID_ON",
      "SSMCS_EQ_MID_Q",
      "SSMCS_EQ_MID_FREQ",
      "SSMCS_EQ_MID_GAIN",
      SSMCS_FREQ_RAW_MIN,
      SSMCS_FREQ_RAW_MAX,
    );
    pushSsmcsBand(
      out,
      y,
      eq.high,
      "SSMCS_EQ_HIGH_ON",
      null,
      "SSMCS_EQ_HIGH_FREQ",
      "SSMCS_EQ_HIGH_GAIN",
      SSMCS_EQ_HIGH_FREQ_RAW_MIN,
      SSMCS_FREQ_RAW_MAX,
    );
  }
}

// Push the value-set commands for one node's PEQ bands (input or output). Each
// band emits only the fields the plan set; a fixed-peaking mid band (type null)
// never writes a filter type. A linked control (MIX) writes both L/R instances.
function pushEqBandCommands(out: VdCommand[], ctrl: EqControl, bands: EqBand[]): void {
  for (const band of ctrl.bands) {
    const v = bands[band.index];
    if (!v) continue;
    const [q, freq, gain] = eqBandFields(band.index).map(dynRule);
    for (const inst of ctrl.instances) {
      if (v.on !== undefined) out.push(rawCommand("EQ_BAND_ON", band.on, "bool", inst, v.on ? 1 : 0));
      if (v.type !== undefined && band.type !== null) {
        const opts = band.name === "low" ? EQ_TYPE_LOW_OPTIONS : EQ_TYPE_HIGH_OPTIONS;
        out.push(rawCommand("EQ_BAND_TYPE", band.type, "enum", inst, boundEnum(v.type, opts, EQ_TYPE_SHELVING)));
      }
      if (v.q !== undefined) out.push(rawCommand("EQ_BAND_Q", band.q, "q", inst, admitLeaf(q, v.q)));
      if (v.freq !== undefined)
        out.push(rawCommand("EQ_BAND_FREQ", band.freq, "eqFreq", inst, admitLeaf(freq, v.freq)));
      if (v.gain !== undefined)
        out.push(rawCommand("EQ_BAND_GAIN", band.gain, "eqGain", inst, admitLeaf(gain, v.gain)));
    }
  }
}

// Push the EQ 1-knob commands (ON / TYPE / LEVEL) for one node's EQ, on every
// linked instance. When 1-knob is on the device drives the 4-band PEQ, so the
// caller skips the band commands — the bands are device-driven, not authored.
// ON / TYPE / LEVEL is a reset chain, measured on a URX44V: an OFF->ON transition
// discards the type back to Intensity, and any type write discards the level to that
// type's neutral point (Intensity 50, the presets 0). Sending them in this order lands
// all three — but only if all three go out, which is why they carry a `group`: a
// converge round re-sending ON alone would leave the type behind, the next round would
// re-send the type and leave the level behind, and the loop alternates until it runs
// out of rounds. Three links is what makes this one reach a residual; the shorter
// chains behind the other sideEffect params are pinned in translate.test.ts.
function pushEqOneKnobCommands(out: VdCommand[], ctrl: EqOneKnobControl, ok: EqOneKnobParams): void {
  for (const inst of ctrl.instances) {
    const chain: VdCommand[] = [];
    if (ok.on !== undefined) chain.push(rawCommand("EQ_ONE_KNOB_ON", ctrl.on, "bool", inst, ok.on ? 1 : 0));
    // The preset enum is shared across every EQ instance, and every instance offers
    // all three types, so the bound is the instance's own menu.
    if (ok.type !== undefined) {
      const type = boundEnum(ok.type, EQ_ONE_KNOB_TYPE_ALL_OPTIONS, EQ_ONE_KNOB_TYPE_DEFAULT);
      chain.push(rawCommand("EQ_ONE_KNOB_TYPE", ctrl.type, "enum", inst, type));
    }
    if (ok.level !== undefined) {
      const level = boundRaw(planRaw(ok.level, EQ_ONE_KNOB_LEVEL_MIN), EQ_ONE_KNOB_LEVEL_MIN, EQ_ONE_KNOB_LEVEL_MAX);
      chain.push(rawCommand("EQ_ONE_KNOB_LEVEL", ctrl.level, "raw", inst, level));
    }
    // Named after the chain's own head address, so there is one spelling of it. Only a
    // chain of two or more can be broken by a partial re-send.
    if (chain.length > 1) for (const c of chain) c.group = `eq1knob:${cmdAddr(chain[0])}`;
    out.push(...chain);
  }
}

/**
 * Resolve a ducker node's instance index (its parent stereo channel's stereo
 * position, via attachTo), or null if it is not a ducker. The ducker's enable
 * (258) and detail (260-263) all address this y.
 *
 * All four positions were confirmed on a URX44V by sentinel write and LCD read-back
 * (y 0..3 = CH5/6, CH7/8, CH9/10, CH11/12). Pair 0 is a different channel on a URX22
 * (`ch_3_4`), which no URX22 has been read to check; the operator accepted that half
 * on 2026-08-13 from the corroboration recorded in the private ledger's D-1, so it is
 * no longer reported as a guess. Getting it wrong would be quiet — the write and the
 * GR meter move together, since meters.ts keys 119 on the ducker node too — so a
 * URX22 that ever reaches this desk is worth one sentinel write and one LCD read.
 */
export function duckerControl(model: DeviceModel, nodeId: string): { y: number } | null {
  const node = model.nodes.find((n) => n.id === nodeId);
  if (!node || node.kind !== "ducker" || !node.attachTo) return null;
  const y = stereoIndexMap(model).get(node.attachTo);
  return y === undefined ? null : { y };
}

// Device routing-source port-ref namespace. Stereo buses occupy 0x100+ as an
// L/R pair; input channels occupy their flat physical input slot (mono CH1-4 =
// 0-3, then each stereo pair takes two — same axis as the input-source param 22).
// The streaming-source (705/706), USB-output (732-735), monitor-source (719/720),
// output-patch (730/731) and ducker-source (259) selectors all store one of these
// ids. (Verified on URX44V: USB out / ducker source of CH9/10 = slot 8, not the
// node index 6.)
const BUS_PORTS: Record<string, { l: number; r: number }> = {
  "bus.stereo": { l: 256, r: 257 },
  "bus.stream": { l: 258, r: 259 },
  "bus.mix1": { l: 288, r: 289 },
  "bus.mix2": { l: 290, r: 291 },
  "bus.mon1": { l: 336, r: 337 },
  "bus.mon2": { l: 338, r: 339 },
};

/** Resolve a routing source node to its port ids (a channel uses its input slots). */
function sourcePorts(model: DeviceModel, nodeId: string): { l: number; r: number } | null {
  const bus = BUS_PORTS[nodeId];
  if (bus) return bus;
  const slots = channelInputSlots(model, nodeId);
  return slots ? { l: slots[0], r: slots[slots.length - 1] } : null;
}

/** Reverse: which source node owns a port id. Buses match their L/R; the rest are channel slots. */
export function nodeForPort(model: DeviceModel, port: number): string | null {
  for (const [id, p] of Object.entries(BUS_PORTS)) if (p.l === port || p.r === port) return id;
  for (const n of model.nodes) {
    if (n.kind !== "channel") continue;
    const slots = channelInputSlots(model, n.id);
    if (slots && slots.includes(port)) return n.id;
  }
  return null;
}

/** microSD Rec track-pair slot nodes (out.sdrec.t1 …) in track order, with the
 *  two device track indices (y on param 736) each pair fills. Empty on models
 *  without a recorder. */
export function recordSlots(model: DeviceModel): { id: string; trackL: number; trackR: number }[] {
  const out: { id: string; trackL: number; trackR: number }[] = [];
  for (const n of model.nodes) {
    const m = /^out\.sdrec\.t(\d+)$/.exec(n.id);
    if (!m) continue;
    const k = Number(m[1]) - 1;
    out.push({ id: n.id, trackL: 2 * k, trackR: 2 * k + 1 });
  }
  return out;
}

/** Record-source port refs (L/R) for a node feeding an SD Rec track pair: a
 *  STEREO/MIX bus, a stereo channel (its two input slots), or a mono channel pair
 *  (its primary's slot L, the partner's slot R = L+1). Null if not a record source. */
function recordSourcePorts(model: DeviceModel, nodeId: string): { l: number; r: number } | null {
  const bus = BUS_PORTS[nodeId];
  if (bus) return bus;
  const slots = channelInputSlots(model, nodeId);
  if (!slots) return null;
  return slots.length === 2 ? { l: slots[0], r: slots[1] } : { l: slots[0], r: slots[0] + 1 };
}

// Physical input port-ref namespace (distinct from the bus/output one above):
// each selectable input source occupies an L/R mono-port pair. Analog mics are
// 0-3; the 0x100+ blocks are stereo sources. The "All Input" / "All USB DAW"
// menu entries are bulk-set actions, not sources, so they are not listed here.
// (Ports verified on URX44V; URX22/44 assumed to share the scheme.)
const INPUT_PORTS: Record<string, [number, number]> = {
  "in.micline_1_2": [0, 1],
  "in.micline_3_4": [2, 3],
  "in.aux": [256, 257],
  "in.usbmain_a": [512, 513],
  "in.usbmain_b": [514, 515],
  "in.usbmain_c": [516, 517],
  "in.usbdaw_1_2": [544, 545],
  "in.usbdaw_3_4": [546, 547],
  "in.usbdaw_5_6": [548, 549],
  "in.usbdaw_7_8": [550, 551],
  "in.usbdaw_9_10": [552, 553],
  "in.usbdaw_11_12": [554, 555],
  "in.usbsub": [576, 577],
  "in.sdplay": [608, 609],
  "in.hdmi": [624, 625],
};

// Param 22's y axis is the flat physical input slot (0-11): mono CH1-4 take one
// slot each, then each stereo pair takes two (L then R). A channel node maps to
// its slot(s) — one for a mono node, an adjacent L/R pair for a stereo node.
export function channelInputSlots(model: DeviceModel, nodeId: string): number[] | null {
  let slot = 0;
  for (const n of model.nodes) {
    if (n.kind !== "channel") continue;
    const span = isStereoChannel(n.id) ? 2 : 1;
    if (n.id === nodeId) return span === 2 ? [slot, slot + 1] : [slot];
    slot += span;
  }
  return null;
}

/** Input ports for a source node, or null if it is not a selectable input. */
export function inputPorts(nodeId: string): [number, number] | null {
  return INPUT_PORTS[nodeId] ?? null;
}

/** Reverse: which input node owns a port id (matches either its L or R). */
export function inputNodeForPort(port: number): string | null {
  for (const [id, [l, r]] of Object.entries(INPUT_PORTS)) if (l === port || r === port) return id;
  return null;
}

// Exclusive routing selectors driven by one incoming wire whose source resolves
// to a bus/channel port: [destNode, kind, paramL, paramR, yL, yR]. Every selector
// has both an L and an R slot (some reuse one param id at two y instances).
// `sourcePorts` maps the wire's source to its L/R port; the param's own encoding
// applies the tag (streaming) or not. A USB output also takes the two wires of a
// mono pair (monoPairOf), written L = the primary's slot, R = the partner's. Drives
// both emit and readback so the two directions cannot drift. (Input source and
// ducker key are bespoke — different namespace / per-instance shape — and stay
// separate below.)
export const ROUTING_SELECTORS: [string, ConnectionKind, ParamName, ParamName, number, number][] = [
  ["bus.stream", "source", "STREAM_SRC_L", "STREAM_SRC_R", 0, 0],
  ["out.usbmain_a", "patch", "USB_OUT_SRC_A", "USB_OUT_SRC_A", 0, 1],
  ["out.usbmain_b", "patch", "USB_OUT_SRC_B", "USB_OUT_SRC_B", 0, 1],
  ["out.usbmain_c", "patch", "USB_OUT_SRC_C", "USB_OUT_SRC_C", 0, 1],
  ["out.usbsub", "patch", "USB_OUT_SRC_SUB", "USB_OUT_SRC_SUB", 0, 1],
  ["bus.mon1", "source", "MONITOR_SRC_L", "MONITOR_SRC_R", 0, 0],
  ["bus.mon2", "source", "MONITOR_SRC_L", "MONITOR_SRC_R", 1, 1],
  ["out.main", "patch", "OUT_PATCH_MAIN", "OUT_PATCH_MAIN", 0, 1],
  ["out.line", "patch", "OUT_PATCH_LINE", "OUT_PATCH_LINE", 0, 1],
];

// OSC → bus assign on/off per output channel: which param + L/R instance (FX is
// mono, r = null). The oscillator can feed several buses, each independently.
export interface OscAssign {
  name: ParamName;
  l: number;
  r: number | null;
}
const OSC_ASSIGN: Record<string, OscAssign> = {
  "bus.stereo": { name: "OSC_ASSIGN_STEREO", l: 0, r: 1 },
  "bus.mix1": { name: "OSC_ASSIGN_MIX", l: 0, r: 1 },
  "bus.mix2": { name: "OSC_ASSIGN_MIX", l: 2, r: 3 },
  "bus.fx1": { name: "OSC_ASSIGN_FX", l: 0, r: null },
  "bus.fx2": { name: "OSC_ASSIGN_FX", l: 1, r: null },
};

/** OSC assign target for a bus, or null if the bus is not an OSC destination. */
export function oscAssign(busId: string): OscAssign | null {
  return OSC_ASSIGN[busId] ?? null;
}

/** Bus ids the oscillator can be assigned to, in display order. */
export const OSC_ASSIGN_BUSES = Object.keys(OSC_ASSIGN);

/** Insert FX for a node: which param, the instance(s) it writes, and its options. */
export interface InsertFxControl {
  param: number;
  /** The matching insert FX ON/OFF (bypass) param — 134 / 577 / 670. */
  onParam: number;
  /** Output bus (STEREO / MIX) rather than an input channel — the two use
   *  different option menus and different engine arrays. */
  isOutput: boolean;
  instances: number[];
  options: InsertFxOption[];
}

// MIX bus insert FX shares param 671 (output axis); each stereo MIX occupies the
// same L/R instance pair as its fader.
const MIX_INSERT_FX_INSTANCES: Record<string, number[]> = {
  "bus.mix1": [0, 1],
  "bus.mix2": [2, 3],
};

/**
 * Insert FX for a node, or null if it has none. The MONO IN channels (mono CH1-4)
 * carry the input effects (param 135); the STEREO master (578) and MIX buses
 * (671, L/R-linked) carry the output effects. Stereo input channels have none.
 */
export function insertFxControl(model: DeviceModel, nodeId: string): InsertFxControl | null {
  if (nodeId === "bus.stereo") {
    return {
      param: PARAMS.OUTPUT_INSERT_FX_STEREO.id,
      onParam: PARAMS.OUTPUT_INSERT_FX_ON_STEREO.id,
      isOutput: true,
      instances: [0],
      options: OUTPUT_INSERT_FX_OPTIONS,
    };
  }
  const mix = MIX_INSERT_FX_INSTANCES[nodeId];
  if (mix) {
    return {
      param: PARAMS.OUTPUT_INSERT_FX_MIX.id,
      onParam: PARAMS.OUTPUT_INSERT_FX_ON_MIX.id,
      isOutput: true,
      instances: mix,
      options: OUTPUT_INSERT_FX_OPTIONS,
    };
  }
  const cc = channelControl(model, nodeId);
  if (cc && !isStereoChannel(nodeId)) {
    return {
      param: PARAMS.INSERT_FX.id,
      onParam: PARAMS.INSERT_FX_ON.id,
      isOutput: false,
      instances: [cc.y],
      options: INSERT_FX_OPTIONS,
    };
  }
  return null;
}

/** Which parameters a device write / diff covers: everything the plan implies,
 *  or only what the device stores in a scene ("scene" drops the sceneExternal
 *  catalog entries — see params.ts and core/scene-scope.ts). Diagnostics
 *  (compare / self-test / prepare) always run "all". */
export type WriteScope = "all" | "scene";

/** Bit layout of a packed address key: y in the low ADDR_SHIFT bits, x above it,
 *  paramId above that. The widths hold every address the app can emit with an order
 *  of magnitude to spare (measured over the whole PARAMS catalog and all three
 *  models' default plans: paramId <= 891, x == 0, y <= 18) and keep the key a
 *  non-negative int32, which is what makes the Map lookup cheap. An address outside
 *  them would fold onto another one and silently drop that command's write, so
 *  translate.test.ts pins both the ranges and the injectivity. */
const ADDR_SHIFT = 10;
const ADDR_MASK = (1 << ADDR_SHIFT) - 1;

/**
 * A device address as a map key. Shared with live.ts, whose snapshot and follow
 * index key on it: this decides WHICH COMMANDS COLLAPSE and that decides WHAT THE
 * SNAPSHOT KEYS ON, so a second spelling would let a collapsed command miss its
 * snapshot entry.
 *
 * Packed into one integer rather than a "paramId:x:y" string: measured, the string
 * keying was the collapse's entire cost — +49 µs on a 101 µs buildCommands (782
 * commands), where the packed key builds the same map 4.7x faster. The string form
 * survives as the published one (LiveSync.snapshotEntries, read by the trace probe
 * and the race harness), rendered by formatAddrKey off the cold path.
 */
export const addrKey = (paramId: number, x: number, y: number): number =>
  (paramId << (ADDR_SHIFT * 2)) | (x << ADDR_SHIFT) | y;

export const cmdAddr = (c: VdCommand): number => addrKey(c.paramId, c.x, c.y);

/** The address each reported out-of-range FX value is written to, in the same order.
 *
 *  A caller adopting such a value has to ask whether the DEVICE confirmed the address it was
 *  sent to, and the report from `plan-validate.ts` names a plan key rather than an address.
 *  This is the join, and it lives here because the mapping does: the slot comes from the
 *  descriptor of the type the write path resolves, and the array param from the channel. An
 *  entry is `undefined` where there is no such command to name — a node the model does not
 *  have, a key no type of that channel names, or a problem that is not a `params` one at all
 *  (the effect object and its `type` / `params` fields have no parameter-array address).
 *
 *  The commands are looked up in the emit's own output rather than rebuilt from the catalogue,
 *  so a change to how an FX slot is addressed cannot leave this answering the old address. */
export function paramRangeAddrs(model: DeviceModel, plan: Plan, problems: ParamRangeProblem[]): (number | undefined)[] {
  const byNodeSlot = new Map<string, number>();
  for (const c of planToCommands(model, plan)) {
    if (c.name === "FX_EFFECT_PARAM" && c.node !== undefined) byNodeSlot.set(`${c.node}/${c.y}`, cmdAddr(c));
  }
  return problems.map((p) => {
    if (p.where !== "params") return undefined;
    const fxIndex = FX_CHANNEL_NODE_INDEX[p.node];
    if (fxIndex === undefined) return undefined;
    const type = resolveFxEffectType(fxIndex, plan.nodeParams[p.node]?.fxEffect?.type);
    const slot = fxParams(type).find((d) => d.key === p.key)?.slot;
    return slot === undefined ? undefined : byNodeSlot.get(`${p.node}/${slot}`);
  });
}

/** A packed key back as "paramId:x:y" — the form the trace probe and the race
 *  harness read off LiveSync.snapshotEntries(). Probe-only, so it stays out of
 *  every hot path that builds or looks up a key. */
export const formatAddrKey = (key: number): string =>
  `${key >>> (ADDR_SHIFT * 2)}:${(key >>> ADDR_SHIFT) & ADDR_MASK}:${key & ADDR_MASK}`;

/**
 * Collapse a repeated device address down to ONE command, keeping the LAST.
 *
 * An insert effect's parameters live in one engine array per effect FAMILY,
 * addressed `engine:0:slot` with no channel axis (see insert-fx-effect.ts), so
 * two nodes holding the same family emit the same addresses carrying their own
 * values. Last-wins matches what the device ends up holding either way: an
 * ordered sendCommands (client.ts) applies the commands in list order, so the
 * last one is the value left standing on the unit.
 *
 * The survivor stays at its OWN index rather than being hoisted to the first
 * occurrence's: a type selector repopulates the engine array it binds with that
 * type's defaults (insert-fx-effect.ts), so a hoisted survivor would be written
 * before the later owner's selector and erased by it, leaving the unit holding
 * neither owner's value.
 *
 * A dropped command whose value the survivor already carries is no loss (the
 * state every device readback produces), so only differing ones are stamped onto
 * the survivor as `shadowed` — what the surfaces report.
 */
export function collapseSharedAddrs(commands: VdCommand[]): VdCommand[] {
  const keys = new Int32Array(commands.length);
  const lastIndex = new Map<number, number>();
  let repeated = false;
  for (let i = 0; i < commands.length; i++) {
    const key = cmdAddr(commands[i]);
    keys[i] = key;
    if (lastIndex.has(key)) repeated = true;
    lastIndex.set(key, i);
  }
  if (!repeated) return commands;
  const shadowed = new Map<number, string[]>();
  const out: VdCommand[] = [];
  // One forward pass: lastIndex holds the LAST index of an address, so every
  // shadowed owner sits before its survivor and its list is complete by the time
  // the survivor is reached.
  for (let i = 0; i < commands.length; i++) {
    const keep = lastIndex.get(keys[i])!;
    if (keep !== i) {
      if (commands[i].vdValue !== commands[keep].vdValue) {
        // Emission order, duplicates kept as-is: a group whose owner repeats stays
        // visible rather than being filtered into silence.
        const owner = commands[i].node ?? "?";
        const list = shadowed.get(keep);
        if (list) list.push(owner);
        else shadowed.set(keep, [owner]);
      }
      continue;
    }
    const dropped = shadowed.get(i);
    out.push(dropped ? { ...commands[i], shadowed: dropped } : commands[i]);
  }
  return out;
}

/** One address's set of plan owners: the one whose value reaches the device, and
 *  the ones whose differing values the collapse dropped. */
export interface SharedOwners {
  /** Owner node whose command survived (undefined for a global address). */
  kept?: string;
  /** Owner nodes whose commands were dropped, in emission order. */
  dropped: string[];
}

/** Identity of one owner set. The grouping below and the report latch (collisionKey)
 *  spell it here rather than each for itself: two spellings that drift make live.ts
 *  group by one rule and compare by another, so a standing collision either
 *  re-reports every flush or a new one is swallowed. */
const ownerKey = (kept: string | undefined, dropped: readonly string[]): string => `${kept ?? ""}<${dropped.join("+")}`;

/** The owner sets a collapsed command list reports, grouped so the several slots
 *  one shared engine array carries read as ONE collision, in emission order.
 *  A survivor with no owner node is a global address: nothing global collides
 *  today, and one that did would want a message of its own rather than this
 *  node-names-two-nodes one, so it is skipped instead of naming an empty node. */
export function collisionOwners(commands: readonly VdCommand[]): SharedOwners[] {
  const groups: SharedOwners[] = [];
  const seen = new Set<string>();
  for (const c of commands) {
    if (!c.shadowed?.length || !c.node) continue;
    const key = ownerKey(c.node, c.shadowed);
    if (seen.has(key)) continue;
    seen.add(key);
    groups.push({ kept: c.node, dropped: [...c.shadowed] });
  }
  return groups;
}

/** Language-independent identity of an owner set — what a report latches on.
 *  Values are deliberately excluded, so a drag does not re-report while a change
 *  of contenders does. Empty string when nothing collided. */
export function collisionKey(owners: readonly SharedOwners[]): string {
  return owners.map((o) => ownerKey(o.kept, o.dropped)).join(";");
}

/**
 * Translate a plan into the list of vd value-set commands it currently implies.
 * Deterministic and side-effect free; the same plan always yields the same list,
 * so callers can diff it for a confirm-before-send preview. `scope` filters the
 * finished list by ParamName, so the scene boundary cannot drift from the
 * catalog flags — and the same filter scopes every consumer (write diff, live
 * snapshot / flush, follow notify registration) at this one chokepoint.
 *
 * One device address yields exactly one command: a repeated address is collapsed
 * to its last (collapseSharedAddrs), before the scope filter so the scene subset
 * stays the full list filtered by ParamName. What that dropped is reported by the
 * live status line, the write confirm and the compare report.
 */
export interface EmitOptions {
  /**
   * Also emit the values the **device** drives, which this function otherwise leaves
   * alone — today the 4-band PEQ while EQ 1-knob is on. Not authoring them is the
   * right default and stays it: the app must not model what the unit computes.
   *
   * The self-test's restore is the one caller that needs them, and for a different
   * reason than mirroring. Putting a unit back exactly as it was found is not the same
   * act as writing a plan onto it: a unit found with 1-knob ON has its bands written by
   * every sweep pass (the sweep flips 1-knob off) and emitted by no restore, so its
   * VISIBLE EQ ends changed under a verdict that says it was restored.
   *
   * Measured on a URX44V before relying on it (2026-08-10): a band gain written while
   * 1-knob is on is accepted (response_code 200) and still holds 1.5 s later — the unit
   * does not re-derive over it. Ordering is what matters instead: writing the 1-knob
   * LEVEL while it is on makes the unit recompute base x level/50, so the bands must go
   * out AFTER the 1-knob chain — which is the order below, and which a probe that got
   * it backwards demonstrated by leaving a band at a fifth of its value.
   */
  includeDeviceDriven?: boolean;
  /**
   * Also emit the input stage of every jack and every D.Gain source the model has, whether
   * or not a channel reads it. A write sends only the ones some channel reads; Compare with
   * device, which reads and never writes, compares them all.
   */
  everyInputHolder?: boolean;
}

/**
 * The insert-FX value the device path acts on for a node: what the plan holds, or No Effect
 * where the node's own control does not carry it.
 *
 * An off-menu value is coerced rather than written — the device would take it verbatim and
 * could bind an effect the plan never named — and it reaches a plan through a file, a
 * `?plan=` link or a device read, none of which the loader gates. **Every surface that
 * offers to EDIT the held effect has to ask this too**: a bus holding a channel effect made
 * the tuning screen open a live editor over an engine array that never receives a single
 * write, which is the split this one answer exists to close.
 */
export function effectiveInsertFx(model: DeviceModel, plan: Plan, nodeId: string): number | undefined {
  const ifx = insertFxControl(model, nodeId);
  const v = plan.nodeParams[nodeId]?.insertFx;
  if (!ifx || v === undefined) return undefined;
  return ifx.options.some((o) => o.value === v) ? v : INSERT_FX_NONE;
}

/** What a write makes of one node's insert FX, in the terms the WIRE sees. */
export interface InsertFxWireState {
  /** The selector as sent: an off-menu value is No Effect, which is what `effectiveInsertFx`
   *  already decides for every surface that asks. */
  selector: number;
  /** The bypass as sent, or null where the write does not send it at all — with No Effect
   *  selected the unit ignores the switch, so the plan's value never leaves. */
  on: number | null;
  /** The engine commands, addressed and encoded, in emit order. Empty for a selector with
   *  no family: No Effect writes no engine parameter. */
  params: string[];
}

/**
 * The insert-FX state a write would leave on the unit for one node.
 *
 * Its reason for existing is comparison — two documents, or the two members of a STEREO-linked
 * pair, differ in a way that MATTERS only where this differs. Stored values do not answer that:
 * an off-menu selector and No Effect are one state, `true` and `1` are one bypass, a bypass
 * beside No Effect is not sent at all, and an engine value belonging to another family is not
 * sent either. So it is computed HERE, from the same helpers the emit loop below uses, rather
 * than modelled a second time somewhere a rule change would not reach.
 *
 * Null where the node has no insert FX, or where the document says nothing about it — the
 * loop below skips such a node too. Callers comparing documents should fill the plan first,
 * since the write sees a completed one.
 */
export function insertFxWireState(model: DeviceModel, plan: Plan, nodeId: string): InsertFxWireState | null {
  const ifx = insertFxControl(model, nodeId);
  const np = plan.nodeParams[nodeId] ?? {};
  if (!ifx || np.insertFx === undefined) return null;
  const selector = effectiveInsertFx(model, plan, nodeId) ?? INSERT_FX_NONE;
  const out: VdCommand[] = [];
  const fam = insertFxFamilyOf(selector);
  if (fam) pushInsertFxEffectCommands(out, insertFxEngine(fam, ifx.isOutput), fam, np.insertFxParams, false);
  return {
    selector,
    on: np.insertFxOn !== undefined && selector !== INSERT_FX_NONE ? (np.insertFxOn ? 1 : 0) : null,
    params: out.map((c) => `${c.name}:${c.paramId}:${c.x}:${c.y}:${c.vdValue}`),
  };
}

export function planToCommands(
  model: DeviceModel,
  plan: Plan,
  scope: WriteScope = "all",
  emit: EmitOptions = {},
): VdCommand[] {
  const commands = collapseSharedAddrs(buildCommands(model, plan, emit));
  if (scope === "all") return commands;
  return commands.filter((c) => (PARAMS[c.name] as ParamSpec).sceneExternal !== true);
}

/**
 * The commands a plan implies BEFORE the collapse — the only view in which a shared
 * address is visible AS a repeat. Exported for the contract test that pins which
 * families share one: asked of planToCommands, "no address repeats" and "one ParamName
 * per address" are true by construction of the collapse and pin nothing.
 *
 * Nothing that talks to a device may use this: a repeated address sends the losing
 * owner's value and then overwrites it, which is the defect the collapse removes.
 */

/** The plan, with its node parameters recording every read of a value they carry.
 *
 *  A Proxy rather than a key threaded through each of the emit's hundred-odd command sites:
 *  the value a command is built from is READ immediately before it is built, so the read is
 *  where the two meet, and recording it there keeps them from drifting the way a second
 *  spelling kept by hand at every site would.
 *
 *  Every string property, carried or not: the emit asks for a key before it decides whether to
 *  send one, and asking is how it finds out. What keeps an absent key from naming the command
 *  that follows it is the value test where the command is built — the key held nothing, so it
 *  is not the value that went out. A prototype method fails the same test. */
function recordingParams(
  nodeParams: Plan["nodeParams"],
  read: (name: string, value: unknown) => void,
): Plan["nodeParams"] {
  const out: Plan["nodeParams"] = {};
  for (const [nodeId, params] of Object.entries(nodeParams)) {
    out[nodeId] = recording(params, (path) => nodeParamContestPath(nodeId, path.join(".")), read) as NodeParams;
  }
  return out;
}

/** The same for each wire's params, named the way the differ names one. A wire's endpoints and
 *  kind are read too, to find it, and are not values a command carries. */
function recordingConnections(
  connections: Plan["connections"],
  read: (name: string, value: unknown) => void,
): Plan["connections"] {
  return connections.map((c) =>
    c.params
      ? {
          ...c,
          params: recording(c.params, (path) => connParamContestKey(c.from, c.to, path.join(".")), read) as ConnParams,
        }
      : c,
  );
}

/** `value`, recording every read of a leaf it carries under the name `nameOf` gives its path. */
function recording(
  value: unknown,
  nameOf: (path: string[]) => string,
  read: (name: string, value: unknown) => void,
  path: string[] = [],
): unknown {
  if (value === null || typeof value !== "object") return value;
  return new Proxy(value as object, {
    get(target, prop, receiver) {
      const held = Reflect.get(target, prop, receiver);
      if (typeof prop !== "string") return held;
      if (held !== null && typeof held === "object") return recording(held, nameOf, read, [...path, prop]);
      read(nameOf([...path, prop]), held);
      return held;
    },
  });
}

/**
 * What produced the value of each command a write under `scope` would send, keyed by address.
 *
 * The join a caller needs to say whether the operator chose what a write is about to change.
 * A key that merely GATES a command — a channel's insert-FX selector, its comp/EQ order —
 * needs no entry of its own: it owns the value of its OWN command, on the same node, so a
 * write that is actually changing the selector already names the strip through it. Read as
 * though a gate owned what it gates, an operator editing one engine parameter under a selector
 * they fetched from the unit is told they are changing something they did not choose, and
 * nothing about that write touches the selector.
 *
 * Answers per address AFTER the collapse, so a shared address answers for the owner that
 * survives it, and under the same scope rule the emit uses. `undefined` for an address whose
 * command carries no record at all — a state the caller must not read as "nobody supplied it".
 */
export function planToCommandOrigins(
  model: DeviceModel,
  plan: Plan,
  scope: WriteScope = "all",
): Map<number, string | null | undefined> {
  const cursor: NonNullable<typeof originCursor> = {};
  const read = (name: string, value: unknown): void => {
    cursor.fresh = { name, value };
  };
  const proxied = {
    ...plan,
    nodeParams: recordingParams(plan.nodeParams, read),
    connections: recordingConnections(plan.connections, read),
    nodeColors: recording(plan.nodeColors, ([id]) => nodeColorContestKey(id), read) as Plan["nodeColors"],
  };
  originCursor = cursor;
  let commands: VdCommand[];
  try {
    commands = buildCommands(model, proxied);
  } finally {
    originCursor = null;
  }
  const origins = new Map<number, string | null | undefined>();
  for (const c of collapseSharedAddrs(commands)) {
    if (scope !== "all" && (PARAMS[c.name] as ParamSpec).sceneExternal === true) continue;
    origins.set(cmdAddr(c), c.origin);
  }
  return origins;
}

export function planToCommandsUncollapsed(model: DeviceModel, plan: Plan): VdCommand[] {
  return buildCommands(model, plan);
}

function buildCommands(model: DeviceModel, plan: Plan, emit: EmitOptions = {}): VdCommand[] {
  const out: VdCommand[] = [];
  // Owner-node stamping for the device-follow index: own(id) attributes every
  // command pushed since the last own() call to `id`. `mark` auto-advances, so a
  // single own(id) at the end of each per-node block (or iteration) covers that
  // node's commands — including those pushed by the push* helpers. Any block that
  // can push then `continue` must call own(id) before the continue so its commands
  // are not mis-attributed to the next iteration's node.
  let mark = 0;
  const own = (id: string | undefined): void => {
    for (let i = mark; i < out.length; i++) if (out[i].node === undefined) out[i].node = id;
    mark = out.length;
  };
  // Sample rate (global y0, raw Hz). A top-level plan scalar (always set), so it is
  // emitted unconditionally as absolute state. Writing it re-clocks the hardware;
  // only 766 is sent (843 auto-follows). See params.ts SAMPLE_RATE.
  //
  // Emitted FIRST so the rest of the write lands on a device already clocked the way
  // the plan says. Re-clocking is the largest single thing a write can do to the
  // hardware, and everything after it is a value meant to hold under that clock.
  // Confirmed on a URX44V: a rate-changing write completes, with the commands that
  // follow the re-clock all reaching the device.
  out.push(command("SAMPLE_RATE", 0, plan.sampleRate));
  own(undefined);

  // Channel node parameters: ON / HPF / polarity / COMP-EQ type / Rec Point / dynamics.
  for (const node of model.nodes) {
    if (node.kind !== "channel") continue;
    const np = plan.nodeParams[node.id];
    if (!np) continue;
    const cc = channelControl(model, node.id);
    if (!cc) continue;
    if (np.on !== undefined) out.push(rawCommand("CH_ON", cc.on, "bool", cc.y, np.on ? 1 : 0));
    if (cc.hasHpf && np.hpf !== undefined) out.push(command("HPF_ON", cc.y, np.hpf ? 1 : 0));
    if (cc.hasHpf && np.hpfFreq !== undefined) out.push(command("HPF_FREQ", cc.y, np.hpfFreq));
    // Polarity invert: one toggle (mono) or two independent L/R (stereo).
    for (const ph of cc.phases) {
      const v = np[ph.key];
      if (v !== undefined) out.push(rawCommand(ph.name, ph.param, "bool", ph.y, v ? 1 : 0));
    }
    // COMP/EQ type (COMP->EQ vs SSMCS) is a MONO IN channel feature (= mic strip).
    if (cc.hasMicStrip && np.compEqType !== undefined)
      out.push(command("COMP_EQ_TYPE", cc.y, boundEnum(np.compEqType, COMP_EQ_OPTIONS, COMP_EQ_COMP_FIRST)));
    // Rec Point: per-channel record / direct-out tap (cc.recPoint = mono 137 /
    // stereo 264, resolved by channelControl like fader/on/pan).
    if (np.recPoint !== undefined) {
      const ssmcs = cc.hasMicStrip && (np.compEqType ?? COMP_EQ_COMP_FIRST) === COMP_EQ_SSMCS;
      out.push(
        rawCommand(
          "REC_POINT",
          cc.recPoint,
          "enum",
          cc.y,
          admitLeaf(recPointRule(!cc.hasMicStrip, ssmcs), np.recPoint),
        ),
      );
    }
    // Channel-strip section ON (GATE/COMP/EQ). The active COMP/EQ bank follows the
    // type; polarity per toggle. Stereo channels expose only EQ.
    for (const sec of channelSections(model, node.id, np.compEqType ?? COMP_EQ_COMP_FIRST)) {
      const v = np[sec.key];
      if (v !== undefined) out.push(rawCommand(sec.name, sec.param, "bool", sec.y, v ? sec.onValue : 1 - sec.onValue));
    }
    // Input EQ 1-knob (mono COMP->EQ / stereo channels). When on, the device
    // drives the bands, so the band commands below are skipped.
    const iok = eqOneKnob(model, node.id, np.compEqType ?? COMP_EQ_COMP_FIRST);
    if (iok && np.eqOneKnob) pushEqOneKnobCommands(out, iok, np.eqOneKnob);
    // Input 4-band PEQ band values (mono COMP->EQ mode / stereo channels).
    const ieq = inputEq(model, node.id, np.compEqType ?? COMP_EQ_COMP_FIRST);
    if (ieq && np.eqBands && (emit.includeDeviceDriven || !np.eqOneKnob?.on)) pushEqBandCommands(out, ieq, np.eqBands);
    // Input GATE / COMP detail values (MONO IN channels; COMP only in COMP->EQ).
    const dyn = channelDynamics(model, node.id, np.compEqType ?? COMP_EQ_COMP_FIRST);
    if (dyn) {
      if (np.gate) pushDynCommands(out, dyn.gate, dyn.y, np.gate as Record<string, number | undefined>);
      if (dyn.comp && np.comp) {
        // While the COMP 1-knob or Auto Makeup is on the device owns the values
        // `compDeviceDriven` names, so they are skipped for the same reason the EQ bands are
        // above. Emitting them is not merely redundant: anything that re-sends the plan's copy
        // after the unit has computed puts the operator's earlier values back on the unit,
        // which is what a converge sharing the flush does (it reads the unit, sees the
        // computed values differ from the plan, and writes the plan's). The two switches and
        // the level stay authored.
        const driven = emit.includeDeviceDriven ? new Set<string>() : compDeviceDriven(np.comp);
        const deviceDriven = (key: string): boolean => driven.has(key);
        pushDynCommands(
          out,
          dyn.comp.filter((f) => !deviceDriven(f.key)),
          dyn.y,
          np.comp as Record<string, number | undefined>,
        );
        if (np.comp.knee !== undefined && !deviceDriven("knee"))
          out.push(command("COMP_KNEE", dyn.y, boundEnum(np.comp.knee, COMP_KNEE_OPTIONS, COMP_KNEE_DEFAULT)));
        if (np.comp.autoMakeup !== undefined) out.push(command("COMP_AUTO_MAKEUP", dyn.y, np.comp.autoMakeup ? 1 : 0));
        if (np.comp.oneKnob !== undefined) out.push(command("COMP_ONE_KNOB", dyn.y, np.comp.oneKnob ? 1 : 0));
        // COMP 1-knob level is a passthrough 0..100 raw (enum encoding), bounded here.
        if (isRaw(np.comp.oneKnobLevel))
          out.push(command("COMP_ONE_KNOB_LEVEL", dyn.y, boundRaw(np.comp.oneKnobLevel, 0, 100)));
      }
      // SSMCS detail (MONO IN, SSMCS mode). Comp/EQ section ON are emitted above
      // via channelSections (compOn/eqOn). Sweet Spot Data is a string param, so it
      // rides the string-write path (collectSsmcsPresetWrites), not this numeric
      // catalog. All values here raw.
      if (!dyn.comp && cc.hasMicStrip && (np.compEqType ?? COMP_EQ_COMP_FIRST) === COMP_EQ_SSMCS) {
        pushSsmcsCommands(out, dyn.y, np.ssmcs);
      }
    }
    own(node.id);
  }

  // MONO IN pair-level CH SETTING: Signal Type (stereo link) and PAN/BAL mode, both
  // held on the pair's primary (odd) channel. Signal Type (23) writes BOTH channels'
  // input indices (1 = STEREO link / 0 = MONO x2); PAN/BAL (891) writes the primary's
  // index only (0 = PAN / 1 = BAL). Both reset dependent device state, so live must
  // converge. Commands are attributed to the primary node.
  //
  // Emitted BEFORE the two connection blocks below: switching either selector makes
  // the unit slam the pair's CH_PAN and every bus send's pan (measured — BAL→PAN hard-
  // pans to ±63, PAN→BAL and unlinking centre), so a pan written ahead of the selector
  // is discarded by the device. The selector leads and the plan's pans land on top.
  for (const [primary, secondary] of model.channelPairs) {
    const np = plan.nodeParams[primary];
    if (!np) continue;
    const py = channelControl(model, primary)?.y;
    const sy = channelControl(model, secondary)?.y;
    if (py === undefined) continue;
    if (np.stereoLink !== undefined && sy !== undefined) {
      out.push(command("SIGNAL_TYPE", py, np.stereoLink ? 1 : 0));
      out.push(command("SIGNAL_TYPE", sy, np.stereoLink ? 1 : 0));
    }
    if (np.panBal !== undefined) out.push(command("PAN_BAL", py, boundEnum(np.panBal, PAN_BAL_OPTIONS, PAN_BAL_PAN)));
    own(primary);
  }

  // The MIX buses' BUS Type (587, both out instances) and Pan Link (589, the L instance: MIX1 =
  // 0, MIX2 = 2), in that order and ahead of every pan and send below. Writing BUS Type turns
  // the MIX's Pan Link off, so the switch goes after it. It also resets the MIX's whole send
  // bank, later than the sends written behind it in the same pass, and the converge a BUS Type
  // write takes puts the bank back in the round after. While Pan Link is on, the unit sets every
  // send pan into that MIX to its source's own PAN / BAL, keeps it there as the source moves,
  // and moves the source when a send pan is written — all without a notify. So the send pans
  // into a linked MIX are not written at all, a restore's device-driven values included: the
  // unit derives them from the source pans written below, and a send pan written there would
  // move its source instead. A switch-off lands after the send pans written behind it in the
  // same pass, so each of them moves its source; the converge's next round reads that source
  // back as a residual and puts it back.
  const panLinked = new Set<string>();
  for (const node of model.nodes) {
    const mix = MIX_FADER_INSTANCES[node.id];
    if (!mix) continue;
    const np = plan.nodeParams[node.id];
    if (np?.busType !== undefined)
      for (const inst of mix)
        out.push(command("BUS_TYPE", inst, boundEnum(np.busType, BUS_TYPE_OPTIONS, BUS_TYPE_VARI)));
    const panLink = np?.panLink;
    if (panLink !== undefined) {
      if (panLink) panLinked.add(node.id);
      out.push(command("PAN_LINK", mix[0], panLink ? 1 : 0));
    }
    own(node.id);
  }

  for (const conn of plan.connections) {
    // Fixed main path into STEREO: the channel's CH_FADER / CH_PAN, or the FX
    // channel's FX_CHANNEL_FADER / FX_CHANNEL_BAL — the source's main level / pan.
    if (parseRef(conn.to).nodeId !== "bus.stereo" || !isFixedConnection(model, conn.from, conn.to)) continue;
    const fromId = parseRef(conn.from).nodeId;
    const cc = channelControl(model, fromId);
    if (cc) {
      out.push(rawCommand("CH_FADER", cc.fader, "level", cc.y, conn.params?.level ?? SEND_LEVEL_UNNAMED_DB));
      out.push(rawCommand("CH_PAN", cc.pan, "pan", cc.y, conn.params?.pan ?? 0));
      // → STEREO bus assign ON (post-fader, firmware V1.3). Ships ON; distinct from
      // the channel master CH_ON (emitted above from np.on).
      out.push(rawCommand("STEREO_ASSIGN_ON", cc.stereoOn, "bool", cc.y, (conn.params?.on ?? true) ? 1 : 0));
    } else {
      const fxY = fxChannelIndex(fromId);
      const mixL = MIX_FADER_INSTANCES[fromId]?.[0];
      if (fxY !== null) {
        out.push(
          rawCommand(
            "FX_CHANNEL_FADER",
            PARAMS.FX_CHANNEL_FADER.id,
            "level",
            fxY,
            conn.params?.level ?? SEND_LEVEL_UNNAMED_DB,
          ),
        );
        out.push(rawCommand("FX_CHANNEL_BAL", PARAMS.FX_CHANNEL_BAL.id, "pan", fxY, conn.params?.pan ?? 0));
        out.push(rawCommand("STEREO_ASSIGN_ON", FX_STEREO_ASSIGN_ON, "bool", fxY, (conn.params?.on ?? true) ? 1 : 0));
      } else if (mixL !== undefined) {
        // MIX 1/2 → STEREO "TO ST": an ON/OFF switch at the MIX's L instance (off
        // by default, factory). No level/pan — the send is fixed routing.
        out.push(command("TO_ST", mixL, (conn.params?.on ?? false) ? 1 : 0));
      }
    }
    own(fromId);
  }

  // CH / FX-channel → MIX/FX bus sends — written as absolute state over every
  // send-capable pair. Every send is fixed (always wired), so its routing is a
  // constant and its on/off lives in conn.params.on (SEND_ON = params.on ?? true);
  // a pair the plan is missing a wire for (an old plan loaded without the fixed
  // seed) is treated as off. Params: level / pan(BAL) / PRE-POST tap; MIX writes
  // both linked L/R instances.
  for (const node of model.nodes) {
    if (node.kind !== "channel" && fxChannelIndex(node.id) === null) continue;
    for (const bus of model.nodes) {
      if (bus.kind !== "bus") continue;
      const sc = sendControl(model, node.id, bus.id);
      if (!sc) continue;
      const conn = plan.connections.find((c) => c.from === ref(node.id, "out") && c.to === ref(bus.id, "in"));
      if (!conn) {
        for (const p of sc.on) out.push(rawCommand("SEND_ON", p, "bool", sc.y, 0));
        continue;
      }
      for (const p of sc.level)
        out.push(rawCommand("SEND_LEVEL", p, "level", sc.y, conn.params?.level ?? SEND_LEVEL_UNNAMED_DB));
      if (!panLinked.has(bus.id))
        for (const p of sc.pan) out.push(rawCommand("SEND_PAN", p, "pan", sc.y, conn.params?.pan ?? 0));
      for (const p of sc.on) out.push(rawCommand("SEND_ON", p, "bool", sc.y, (conn.params?.on ?? true) ? 1 : 0));
      // CH -> FX taps are read-only (broker max_value=0 rejects a PRE write); they
      // are read back but never written. Other taps are settable. See sendTapWritable.
      if (sendTapWritable(model, conn.from, conn.to))
        out.push(rawCommand("SEND_TAP", sc.tap, "bool", sc.y, conn.params?.tap === "pre" ? 1 : 0));
    }
    own(node.id);
  }

  // Bus output faders: STEREO master (581, single) and MIX (674, L/R-linked).
  for (const node of model.nodes) {
    if (node.kind !== "bus") continue;
    const bf = busFader(node.id);
    const np = plan.nodeParams[node.id];
    if (!bf || np?.level === undefined) continue;
    for (const yi of bf.instances) out.push(rawCommand(bf.name, bf.param, "level", yi, np.level));
    own(node.id);
  }

  // Bus master balance / pan: STEREO master (583, single) and MIX (676, L/R-linked).
  for (const node of model.nodes) {
    if (node.kind !== "bus") continue;
    const bb = busBalance(node.id);
    const np = plan.nodeParams[node.id];
    if (!bb || np?.pan === undefined) continue;
    for (const yi of bb.instances) out.push(rawCommand(bb.name, bb.param, "pan", yi, np.pan));
    own(node.id);
  }

  // FX-channel effects: EFFECT TYPE + parameter array for each FX channel the plan
  // DESCRIBES. An absent fxEffect writes nothing — and the load path never leaves one
  // absent, since `fillFactoryParams` completes a document from the model's factory
  // values, so what reaches here without one is a plan no loader touched. The EFFECT
  // TYPE write is not recoverable: it refills the engine array with that type's
  // defaults, and selecting the old type back does not bring the old values with it,
  // which is what the write confirm names the affected strips for.
  // Once the section IS present the whole channel is authored, selector included: the
  // array is absolute state, and a type write would refill the slots the plan left out
  // anyway. There is no partial FX write.
  for (const node of model.nodes) {
    const fxY = fxChannelIndex(node.id);
    if (fxY === null) continue;
    const fx = plan.nodeParams[node.id]?.fxEffect;
    if (fx) pushFxEffectCommands(out, fxY, fx, emit.includeDeviceDriven === true);
    own(node.id);
  }

  // Insert FX (enum): mono input channels (135) and output buses (578 / 671).
  // When the selected effect has editable parameters (guitar amp / pitch fix /
  // compander / multi-band comp), also emit its engine parameter array. The
  // selector binds + populates the engine; the array writes override with the
  // plan's values (absolute state, like the FX-channel effects). The ON/OFF
  // (bypass) switch is emitted after the selector AND the restored engine slots:
  // the device auto-engages it on every (re)selection, so the plan's intent must
  // land last to stick.
  // With No Effect selected the device ignores the switch, so it is not written.
  for (const node of model.nodes) {
    const ifx = insertFxControl(model, node.id);
    const np = plan.nodeParams[node.id] ?? {};
    if (!ifx || np.insertFx === undefined) continue;
    const v = effectiveInsertFx(model, plan, node.id) ?? INSERT_FX_NONE;
    for (const inst of ifx.instances) out.push(rawCommand("INSERT_FX", ifx.param, "insertFx", inst, v));
    const fam = insertFxFamilyOf(v);
    if (fam)
      pushInsertFxEffectCommands(
        out,
        insertFxEngine(fam, ifx.isOutput),
        fam,
        np.insertFxParams,
        emit.includeDeviceDriven === true,
      );
    if (np.insertFxOn !== undefined && v !== INSERT_FX_NONE) {
      for (const inst of ifx.instances) {
        out.push(rawCommand("INSERT_FX_ON", ifx.onParam, "bool", inst, np.insertFxOn ? 1 : 0));
      }
    }
    own(node.id);
  }

  // Output bus EQ ON: STEREO (498, single) and MIX (591, L/R-linked).
  for (const node of model.nodes) {
    if (node.kind !== "bus") continue;
    const eq = busEqOn(node.id);
    const np = plan.nodeParams[node.id];
    if (!eq || np?.eqOn === undefined) continue;
    for (const inst of eq.instances) out.push(rawCommand(eq.name, eq.param, "bool", inst, np.eqOn ? 1 : 0));
    own(node.id);
  }

  // Output bus EQ 1-knob (STEREO / MIX). When on, the device drives the bands.
  for (const node of model.nodes) {
    if (node.kind !== "bus") continue;
    const ok = plan.nodeParams[node.id]?.eqOneKnob;
    const ctrl = eqOneKnob(model, node.id, COMP_EQ_COMP_FIRST);
    if (ctrl && ok) pushEqOneKnobCommands(out, ctrl, ok);
    own(node.id);
  }

  // Output bus 4-band PEQ band values: STEREO (single) and MIX (L/R-linked).
  // Skipped when 1-knob is on (the device computes the bands).
  for (const node of model.nodes) {
    if (node.kind !== "bus") continue;
    const np = plan.nodeParams[node.id];
    const oeq = outputEq(node.id);
    if (oeq && np?.eqBands && (emit.includeDeviceDriven || !np.eqOneKnob?.on)) pushEqBandCommands(out, oeq, np.eqBands);
    own(node.id);
  }

  // Ducker on/off + detail (threshold/range/attack/decay): one per stereo channel,
  // stored on the ducker node and addressed at its parent channel's stereo index.
  for (const node of model.nodes) {
    if (node.kind !== "ducker") continue;
    const dc = duckerControl(model, node.id);
    const np = plan.nodeParams[node.id];
    if (!dc) continue;
    if (np?.duckerOn !== undefined) out.push(command("DUCKER_ON", dc.y, np.duckerOn ? 1 : 0));
    if (np?.ducker) pushDynCommands(out, DUCKER_FIELDS, dc.y, np.ducker as Record<string, number | undefined>);
    // Ducker key source (259): the incoming key wire picks a channel or bus; emit
    // its port (a channel uses its L input slot, a bus its L port). No key wire
    // emits the NONE sentinel so a write clears the device's key selection.
    const key = incomingConnection(plan, ref(node.id, "in"), "key");
    if (key) {
      const p = sourcePorts(model, parseRef(key.from).nodeId);
      if (p) out.push(command("DUCKER_SRC", dc.y, p.l));
    } else {
      out.push(command("DUCKER_SRC", dc.y, PORT_REF_NONE));
    }
    own(node.id);
  }

  // Input source select — absolute. A source wire picks a physical input, encoded
  // as a raw port ref; no wire emits the NONE sentinel so a write clears the
  // device's selection, matching readback (NONE = no source wire). MONO CH1-4 use
  // param 22 indexed by physical input slot (a mono node fills one slot, even = L /
  // odd = R). Stereo channels use the separate 209/210 (L/R) pair indexed by stereo
  // pair index — param 22 only covers the mono slots (confirmed on URX44V).
  const srcStereoIdx = stereoIndexMap(model);
  for (const node of model.nodes) {
    if (node.kind !== "channel") continue;
    const conn = incomingConnection(plan, ref(node.id, "in"), "source");
    const ports = conn ? inputPorts(parseRef(conn.from).nodeId) : null;
    // A wire to a source not in the input namespace is left untouched.
    if (conn && !ports) continue;
    if (isStereoChannel(node.id)) {
      const si = srcStereoIdx.get(node.id);
      if (si === undefined) continue;
      out.push(command("STEREO_INPUT_SOURCE_L", si, ports ? ports[0] : PORT_REF_NONE));
      out.push(command("STEREO_INPUT_SOURCE_R", si, ports ? ports[1] : PORT_REF_NONE));
    } else {
      const slots = channelInputSlots(model, node.id);
      if (!slots) continue;
      for (const s of slots) out.push(command("INPUT_SOURCE", s, ports ? ports[s & 1] : PORT_REF_NONE));
    }
    own(node.id);
  }

  // The input stage, per MIC/LINE jack and per digital source — the unit keeps it there, and
  // every channel reading one applies it. Sent for the holders some channel reads, after the
  // source selects above, so a write that rewires and sets a value in one pass lands the
  // value on a jack the channel already reads. Each value is bounded to its own address's
  // range: A.Gain to -8..+70 dB, D.Gain to ±24 dB.
  const reads = emit.everyInputHolder ? null : inputStageReadSet(model, plan);
  for (const node of model.nodes) {
    const base = MIC_LINE_JACK_BASE[node.id];
    if (base === undefined) continue;
    const np = plan.nodeParams[node.id];
    if (!np) continue;
    for (const side of ["L", "R"] as const) {
      if (reads && !reads.jacks.get(node.id)?.has(side)) continue;
      const j = base + (side === "R" ? 1 : 0);
      const hasHiZ = (HI_Z_JACKS[model.id] ?? []).includes(j);
      // Whichever of +48V / HI-Z the plan holds off is sent first, so a write taking the
      // unit from one to the other never has both on in between. HI-Z always goes out
      // ahead of A.Gain, which the unit bounds to +40 dB while HI-Z is on.
      const hiZ = hasHiZ ? np[`hiZ${side}`] : undefined;
      const hiZOffFirst = hiZ === false;
      if (hiZOffFirst) out.push(command("HI_Z", j, 0));
      const phantom = np[`phantom${side}`];
      if (phantom !== undefined) out.push(command("PHANTOM", j, phantom ? 1 : 0));
      if (hasHiZ && !hiZOffFirst) {
        const on = np[`hiZ${side}`];
        if (on !== undefined) out.push(command("HI_Z", j, on ? 1 : 0));
      }
      const gain = np[`gain${side}`];
      if (gain !== undefined)
        out.push(rawCommand("HA_GAIN", PARAMS.HA_GAIN.id, "gain", j, clamp(gain, A_GAIN_MIN_DB, A_GAIN_MAX_DB)));
      // Clip Safe goes out after A.Gain: an OFF landing together with an A.Gain write puts the
      // A.Gain back to its earlier value, so the senders hold the OFF until the unit has
      // announced the A.Gain ahead of it (input-lock.ts, `clipSafeOffAwaits`).
      const clipSafe = np[`clipSafe${side}`];
      if (clipSafe !== undefined) out.push(command("CLIP_SAFE", j, clipSafe ? 1 : 0));
    }
    own(node.id);
  }
  for (const node of model.nodes) {
    const address = D_GAIN_SOURCES[node.id];
    if (!address || (reads && !reads.sources.has(node.id))) continue;
    const gain = plan.nodeParams[node.id]?.dGain;
    if (gain === undefined) continue;
    const v = clamp(gain, D_GAIN_MIN_DB, D_GAIN_MAX_DB);
    for (const y of address.instances) out.push(rawCommand("D_GAIN", address.id, "gain", y, v));
    own(node.id);
  }

  // Streaming / USB-out / monitor / analog-patch selects — absolute. One incoming
  // wire → source port(s); the two wires of a mono pair into a USB output → L = the
  // primary's slot, R = the partner's, whatever order the wires are in. No wire emits
  // the NONE sentinel so a write clears the selection — except on a receiver the unit
  // never leaves without a source (STREAMING, whose list has no None), where no wire
  // emits nothing at all and the unit keeps the source it holds (leavesUnsourced;
  // planToFollowOnlyAddrs registers the address instead). Any other set of wires writes
  // its first wire alone. Every source a wire writes is one the routing rules allow or one
  // a device read found the unit holding. At STREAMING a read takes no source outside its
  // list (readback), and every value written there is one the unit's own list offers.
  // Skips selectors whose destination node is absent on this model. See
  // ROUTING_SELECTORS; readback consumes the same table.
  for (const [to, kind, pl, pr, yl, yr] of ROUTING_SELECTORS) {
    if (!model.nodes.some((n) => n.id === to)) continue;
    if (leavesUnsourced(model, plan, to, kind)) continue;
    const wires = plan.connections.filter((c) => c.to === ref(to, "in") && c.kind === kind);
    const pair =
      kind === "patch"
        ? monoPairOf(
            model,
            ref(to, "in"),
            wires.map((c) => c.from),
          )
        : null;
    const conn = wires[0];
    const p = pair
      ? { l: sourcePorts(model, pair[0])!.l, r: sourcePorts(model, pair[1])!.l }
      : conn
        ? sourcePorts(model, parseRef(conn.from).nodeId)
        : null;
    // A wire to a source that does not resolve to a port is left untouched.
    if (conn && !p) continue;
    out.push(command(pl, yl, p ? p.l : PORT_REF_NONE));
    out.push(command(pr, yr, p ? p.r : PORT_REF_NONE));
    own(to);
  }

  // microSD Rec per-track source assign (param 736) — absolute over every track-pair
  // slot. A record wire picks one source (channel pair / STEREO / MIX); its L/R port
  // refs go to the pair's two tracks. No wire writes the NONE sentinel to both,
  // matching readback (NONE = no record wire). Track Count (839) is never emitted —
  // the broker refuses every value above two tracks (see PARAMS.SD_REC_TRACK_COUNT).
  // Absent on models without a recorder (recordSlots is then empty).
  for (const slot of recordSlots(model)) {
    const conn = incomingConnection(plan, ref(slot.id, "in"), "record");
    const p = conn ? recordSourcePorts(model, parseRef(conn.from).nodeId) : null;
    // A wire to a source that does not resolve to a port is left untouched.
    if (conn && !p) continue;
    out.push(command("SD_REC_SOURCE", slot.trackL, p ? p.l : PORT_REF_NONE));
    out.push(command("SD_REC_SOURCE", slot.trackR, p ? p.r : PORT_REF_NONE));
    own(slot.id);
  }

  // Bus master ON/OFF: STEREO master (582, y = 0), MIX buses (675, L/R-linked per
  // MIX) and the FX channels (338, one instance per FX).
  for (const node of model.nodes) {
    if (node.kind !== "bus") continue;
    const bm = busMasterOn(node.id);
    const np = plan.nodeParams[node.id];
    if (!bm || np?.on === undefined) continue;
    for (const inst of bm.instances) out.push(rawCommand(bm.name, bm.param, "bool", inst, np.on ? 1 : 0));
    own(node.id);
  }

  // Monitor bus ON / level / CUE interrupt / MONO: bus.mon1 → y0, bus.mon2 → y1.
  for (const [id, y] of [
    ["bus.mon1", 0],
    ["bus.mon2", 1],
  ] as const) {
    const np = plan.nodeParams[id];
    if (np?.on !== undefined) out.push(command("MONITOR_ON", y, np.on ? 1 : 0));
    if (np?.level !== undefined) out.push(command("MONITOR_LEVEL", y, np.level));
    if (np?.cueInterrupt !== undefined) out.push(command("MONITOR_CUE_INTERRUPT", y, np.cueInterrupt ? 1 : 0));
    if (np?.mono !== undefined) out.push(command("MONITOR_MONO", y, np.mono ? 1 : 0));
    // PHONES level shares the monitor's y axis (PHONES 1 ↔ mon1 = y0, PHONES 2 ↔ mon2 = y1).
    if (np?.phonesLevel !== undefined) out.push(command("PHONES_LEVEL", y, np.phonesLevel));
    own(id);
  }

  // Oscillator generator (bus.osc node): on / level / mode / frequency.
  const osc = plan.nodeParams["bus.osc"]?.osc;
  if (osc?.on !== undefined) out.push(command("OSC_ON", 0, osc.on ? 1 : 0));
  if (osc?.level !== undefined) out.push(command("OSC_LEVEL", 0, osc.level));
  if (osc?.mode !== undefined) out.push(command("OSC_MODE", 0, boundEnum(osc.mode, OSC_MODE_OPTIONS, OSC_MODE_SINE)));
  if (osc?.freq !== undefined) out.push(command("OSC_FREQ", 0, osc.freq));
  // Burst Noise width (seconds → ms ×1000) / interval (seconds, raw). Burst mode
  // only on the device, but emitted whenever present (like freq for Sine). The
  // interval is a passthrough raw (1..30 s), so it carries the emit-path firewall.
  if (osc?.width !== undefined) out.push(command("OSC_BURST_WIDTH", 0, osc.width));
  if (isRaw(osc?.interval)) out.push(command("OSC_BURST_INTERVAL", 0, boundRaw(osc.interval, 1, 30)));
  own("bus.osc");

  // STREAMING DELAY (bus.stream node, global y = 0): on / time / frame rate.
  // Emitted only when the plan carries delay settings, leaving the device's
  // delay untouched otherwise (like the oscillator generator above).
  const delay = plan.nodeParams["bus.stream"]?.delay;
  if (delay?.on !== undefined) out.push(command("STREAM_DELAY_ON", 0, delay.on ? 1 : 0));
  if (delay?.time !== undefined) out.push(command("STREAM_DELAY_TIME", 0, delay.time));
  if (delay?.frameRate !== undefined)
    out.push(
      command(
        "STREAM_DELAY_FRAME_RATE",
        0,
        boundEnum(delay.frameRate, DELAY_FRAME_RATE_OPTIONS, DELAY_FRAME_RATE_DEFAULT),
      ),
    );
  own("bus.stream");

  // OSC → bus assign — absolute over every OSC-assignable bus. A wire turns the
  // destination's L/R channels on (oscL/oscR; absent = on); no wire turns both
  // off, matching readback (both off = no wire). FX buses are mono (R skipped).
  for (const busId of OSC_ASSIGN_BUSES) {
    const a = oscAssign(busId);
    if (!a) continue;
    const conn = plan.connections.find((c) => c.from === ref("bus.osc", "out") && c.to === ref(busId, "in"));
    out.push(command(a.name, a.l, conn && conn.params?.oscL !== false ? 1 : 0));
    if (a.r !== null) out.push(command(a.name, a.r, conn && conn.params?.oscR !== false ? 1 : 0));
    own(busId);
  }

  // CH SETTING color (palette index): input channels (20) and MIX/STEREO buses
  // (586 / 496), written to every linked instance — a palette hex as its index and
  // `COLOR_OFF` as the Off index. A node the plan gives no color sends nothing, and a
  // value that is no plan color is skipped rather than guessed; the load completes an
  // absent color and drops an off-palette one, so neither reaches here from a document.
  for (const node of model.nodes) {
    const cc = colorControl(model, node.id);
    if (!cc) continue;
    const index = planColorIndex(plan.nodeColors[node.id]);
    if (index === null) continue;
    for (const inst of cc.instances) out.push(rawCommand(cc.name, cc.param, "raw", inst, index));
    own(node.id);
  }
  return out;
}

/** Whether the emit leaves a ROUTING_SELECTORS row out: a receiver the unit never leaves
 *  without a source that the plan gives no wire of the selector's kind. Read by the emit's
 *  skip and by the follow-only registration that stands in for it. */
function leavesUnsourced(model: DeviceModel, plan: Plan, to: string, kind: ConnectionKind): boolean {
  return (
    requiresSource(model, ref(to, "in")) && !plan.connections.some((c) => c.to === ref(to, "in") && c.kind === kind)
  );
}

/** One address the app READS but never writes, for the device-follow registration. */
export interface FollowOnlyAddr {
  param: number;
  x: number;
  y: number;
  /** Catalog name, for the follow's per-control accounting. Same convention the
   *  emitted send commands use: the PARAMS entry names the command and its encoding,
   *  while the address is computed per instance. */
  name: ParamName;
  /** Owner node whose scoped read repairs it. Undefined would mean "device-wide", which
   *  routes the notify to a full read — nothing needs that today, and an address that
   *  did would also have to be readable on a scoped read to be worth indexing. */
  node: string;
  /** Take the owner's scoped read for a notify here, even for a param that is otherwise
   *  followed directly: the address is one the emit does not carry. */
  scoped?: true;
}

/**
 * Addresses the unit announces, the app reads, and nothing emits — so `planToCommands`
 * does not carry them and they would be in no notify registration. Registering them is
 * what lets a front-panel change reach the plan instead of waiting for the next full
 * read. Both families were measured announcing a change on a URX44V (2026-08-11, System
 * V1.3.1.0); the addresses that stay silent when the front panel moves them (D.Gain, the
 * insert-FX engine array, and every FX array slot but the one below) are not here, because
 * a registration would do nothing for them.
 *
 * The routing selectors here are the ones the emit leaves out (`leavesUnsourced`) — STREAMING's
 * source while the plan gives it no wire. The unit announces a source picked on its own panel
 * there, one notify per address, as it announces a write there.
 *
 * This lives beside `planToCommands` on purpose: the tap half is the exact complement of
 * the `sendTapWritable` test that suppresses the write there, and the two were written as
 * separate walks over the same (channel, bus) pairs long enough to disagree about which
 * sources count.
 *
 * `scope` is applied with the same `sceneExternal` predicate `planToCommands` uses, for a
 * reason stronger than symmetry: under "scene" a whole-device read RESTORES the plan's
 * scene-external values afterwards (`main.ts` applyDeviceStateScoped → scene-scope.ts), so
 * a follow that pulled one in would be the one path in the app where *Scene only* does not
 * hold — the notify-driven read and the full read would disagree about the same value
 * under the same preference. Track Count and STREAMING's source are `sceneExternal`; the send
 * taps are not.
 */
export function planToFollowOnlyAddrs(model: DeviceModel, plan: Plan, scope: WriteScope = "all"): FollowOnlyAddr[] {
  const out: FollowOnlyAddr[] = [];
  // A receiver the unit never leaves without a source, while the plan holds no wire into
  // it: the emit sends nothing there, and this registers the address in its place.
  for (const [to, kind, pl, pr, yl, yr] of ROUTING_SELECTORS) {
    if (!model.nodes.some((n) => n.id === to) || !leavesUnsourced(model, plan, to, kind)) continue;
    out.push({ param: PARAMS[pl].id, x: 0, y: yl, name: pl, node: to });
    out.push({ param: PARAMS[pr].id, x: 0, y: yr, name: pr, node: to });
  }
  // CH → FX send taps: the broker publishes max_value 0 on 193/197/320/324, so PRE is
  // unwritable and the emit loop above skips SEND_TAP for them. Same source filter as
  // that loop — an FX-channel source cannot reach an unwritable tap today (its sends go
  // to MIX, whose taps are writable), and stating that through `sendTapWritable` rather
  // than by narrowing the walk is what keeps this correct if that ever changes.
  for (const node of model.nodes) {
    if (node.kind !== "channel" && fxChannelIndex(node.id) === null) continue;
    for (const bus of model.nodes) {
      if (bus.kind !== "bus") continue;
      const sc = sendControl(model, node.id, bus.id);
      if (!sc || sendTapWritable(model, ref(node.id, "out"), ref(bus.id, "in"))) continue;
      out.push({ param: sc.tap, x: 0, y: sc.y, name: "SEND_TAP", node: node.id });
    }
  }
  // The COMP gain Auto Makeup is computing right now. The writer leaves it out
  // (`compDeviceDriven`) and the unit announces each recompute there, so it is registered
  // here or nothing hears it. The 1-knob's values need no entry: its level is a refetch head.
  for (const node of model.nodes) {
    const np = plan.nodeParams[node.id];
    const dyn = channelDynamics(model, node.id, np?.compEqType ?? COMP_EQ_COMP_FIRST);
    if (!dyn?.comp || compDeviceDriven(np?.comp) !== COMP_AUTO_MAKEUP_DRIVEN) continue;
    for (const f of dyn.comp) {
      if (COMP_AUTO_MAKEUP_DRIVEN.has(f.key))
        out.push({ param: PARAMS[f.name].id, x: 0, y: dyn.y, name: f.name, node: node.id });
    }
  }
  // The jacks no channel reads. The unit keeps their input stage and announces a panel move
  // there, while the emit carries only the jacks some channel reads — so these are
  // registered here, and a notify takes the MIC/LINE node's scoped read, which keeps the
  // plan's copy current for when a channel is routed there. D.Gain sources are not here: a
  // panel move of one announces nothing.
  const reads = inputStageReadSet(model, plan);
  for (const node of model.nodes) {
    const base = MIC_LINE_JACK_BASE[node.id];
    if (base === undefined) continue;
    for (const side of ["L", "R"] as const) {
      if (reads.jacks.get(node.id)?.has(side)) continue;
      const j = base + (side === "R" ? 1 : 0);
      const names: ParamName[] = ["PHANTOM", "HA_GAIN", "CLIP_SAFE"];
      if ((HI_Z_JACKS[model.id] ?? []).includes(j)) names.push("HI_Z");
      for (const name of names) out.push({ param: PARAMS[name].id, x: 0, y: j, name, node: node.id, scoped: true });
    }
  }
  // microSD Rec Track Count: the broker caps it at 1, so the only values software can
  // reach are "two tracks" and one the unit has no meaning for. Read onto out.sdrec's
  // node params, which is the node whose scoped read repairs it.
  if (model.nodes.some((n) => n.id === "out.sdrec")) {
    out.push({ param: PARAMS.SD_REC_TRACK_COUNT.id, x: 0, y: 0, name: "SD_REC_TRACK_COUNT", node: "out.sdrec" });
  }
  // The FX slots the unit is computing right now. `pushFxEffectCommands` leaves a `computed`
  // slot out of what it sends, and the registration the live layer builds is the emitted set
  // — so without this entry the address is subscribed by nobody while the unit is the one
  // moving it. The unit ANNOUNCES each recompute there, so the value would be announced into
  // silence: the plan would keep the number it had, the screen would print it, and the
  // writer would send it back the moment Sync went off. Plan-dependent, unlike everything above it — which slot this is, and whether there
  // is one at all, is a function of the effect type and of the Sync switch.
  for (const node of model.nodes) {
    const fxIndex = fxChannelIndex(node.id);
    if (fxIndex === null) continue;
    const fx = plan.nodeParams[node.id]?.fxEffect;
    const type = resolveFxEffectType(fxIndex, fx?.type);
    const owners = fxRowOwners(type, fx?.params);
    if (!owners.size) continue;
    for (const desc of fxParams(type)) {
      if (!owners.has(desc.key)) continue;
      out.push({ param: FX_EFFECT_ARRAY_PARAM[fxIndex], x: 0, y: desc.slot, name: "FX_EFFECT_PARAM", node: node.id });
    }
  }
  if (scope === "all") return out;
  return out.filter((f) => (PARAMS[f.name] as ParamSpec).sceneExternal !== true);
}

/** One string write: a node's CH SETTING name, or the SSMCS preset index. */
export interface NameWrite {
  param: number;
  y: number;
  value: string;
  /** The catalog entry this write is, for the string writes that ARE one. A node's name
   *  is not — its address comes from `nameControl` and it has no `PARAMS` row — so it
   *  carries no entry, and the live flush sends it as an ordinary write.
   *  Present so a string write can be a `sideEffect` head at all: the flush reads the
   *  flag off this rather than reverse-mapping a param id back to a catalog row. */
  name?: ParamName;
  /** The node that owns the write — a name and a preset alike — for the reason
   *  `VdCommand.node` exists: a `"refetch"` head is repaired by re-reading its owner, and a
   *  read scoped to some nodes carries the names of those nodes and no others. */
  node: string;
}

/**
 * The CH SETTING name writes a plan implies. Names are strings (outside the
 * numeric VdCommand path), sent via the string IPC. Emitted only for nodes the
 * plan gives an explicit name; an unnamed node is left as the device has it
 * (mirrors how an uncolored node is not written). Each linked instance is set.
 *
 * Normalized here, which is where `boundRaw` sits for the numeric leaves — the load
 * funnel and the rename bound their own inputs, but a name also reaches the plan from
 * a device read and from the unit's own rename, and this is the one place all of them
 * pass through on the way to the wire. It is what `diffNames` compares the device's
 * value against, so a value that is not normalized here can never match one.
 */
/** The nameable nodes the plan gives no name, which `planToNameWrites` sends nothing for — the
 *  unit keeps whatever name it holds there. In the model's order. */
export function unnamedNodes(model: DeviceModel, plan: Plan): string[] {
  return model.nodes.filter((n) => !plan.nodeNames[n.id] && nameControl(model, n.id) !== null).map((n) => n.id);
}

export function planToNameWrites(model: DeviceModel, plan: Plan): NameWrite[] {
  const out: NameWrite[] = [];
  for (const node of model.nodes) {
    const name = plan.nodeNames[node.id];
    if (!name) continue;
    const nc = nameControl(model, node.id);
    if (!nc) continue;
    for (const y of nc.instances) out.push({ param: nc.param, y, value: normalizeNodeName(name), node: node.id });
  }
  // SSMCS Sweet Spot Data preset (param 91): a 4-digit zero-padded string, so it
  // rides this string-write path. MONO IN channels in SSMCS mode that carry an
  // explicit preset index only; the device drives the rest of the strip from it.
  for (const node of model.nodes) {
    const np = plan.nodeParams[node.id];
    const idx = np?.ssmcs?.sweetSpotData;
    if (idx === undefined) continue;
    if ((np?.compEqType ?? COMP_EQ_COMP_FIRST) !== COMP_EQ_SSMCS) continue;
    const cc = channelControl(model, node.id);
    if (!cc?.hasMicStrip) continue;
    out.push({
      param: PARAMS.SWEET_SPOT_DATA.id,
      y: cc.y,
      value: sweetSpotDataToStr(idx),
      name: "SWEET_SPOT_DATA",
      node: node.id,
    });
  }
  return out;
}

// --- [CUE] flags ------------------------------------------------------------
// The address of each cuable strip's [CUE] flag. The flags themselves are live-session
// state outside the plan; cue.ts holds them and applies the unit's rules.

export type CueGroup = "input" | "output";

/** One cuable strip's flag. `mirrors` are the instances the unit sets together with `y` —
 *  read and announced beside it, never written. */
export interface CueAddr {
  node: string;
  param: number;
  y: number;
  group: CueGroup;
  mirrors: readonly number[];
}

const BUS_CUES: readonly CueAddr[] = [
  { node: "bus.fx1", param: PARAMS.CUE_FX.id, y: 0, group: "input", mirrors: [] },
  { node: "bus.fx2", param: PARAMS.CUE_FX.id, y: 1, group: "input", mirrors: [] },
  { node: "bus.mix1", param: PARAMS.CUE_MIX.id, y: 0, group: "output", mirrors: [1] },
  { node: "bus.mix2", param: PARAMS.CUE_MIX.id, y: 2, group: "output", mirrors: [3] },
  { node: "bus.stream", param: PARAMS.CUE_STREAMING.id, y: 0, group: "output", mirrors: [1] },
  { node: "bus.stereo", param: PARAMS.CUE_STEREO.id, y: 0, group: "output", mirrors: [] },
];

const cueAddrCache = new WeakMap<DeviceModel, readonly CueAddr[]>();

/** Every cuable strip of the model, channels first in board order, then the FX channels and
 *  the output group. */
export function cueAddrs(model: DeviceModel): readonly CueAddr[] {
  let list = cueAddrCache.get(model);
  if (list) return list;
  const ids = new Set(model.nodes.map((n) => n.id));
  const out: CueAddr[] = [];
  for (const n of model.nodes) {
    if (n.kind !== "channel") continue;
    const cc = channelControl(model, n.id);
    if (!cc) continue;
    const param = isStereoChannel(n.id) ? PARAMS.CUE_STEREO_CH.id : PARAMS.CUE_MONO.id;
    out.push({ node: n.id, param, y: cc.y, group: "input", mirrors: [] });
  }
  for (const c of BUS_CUES) if (ids.has(c.node)) out.push(c);
  list = out;
  cueAddrCache.set(model, list);
  return list;
}

export function cueAddrOf(model: DeviceModel, node: string): CueAddr | undefined {
  return cueAddrs(model).find((a) => a.node === node);
}

/** The addresses a session registers for notifies: each flag and the instances it mirrors. */
export function cueNotifyAddrs(model: DeviceModel): Array<[number, number, number]> {
  return cueAddrs(model).flatMap((a) => [a.y, ...a.mirrors].map((y): [number, number, number] => [a.param, 0, y]));
}

/** The strip a notify address belongs to, and whether it is one of that flag's mirrors. */
export function cueAt(
  model: DeviceModel,
  paramId: number,
  x: number,
  y: number,
): { addr: CueAddr; mirror: boolean } | undefined {
  if (x !== 0) return undefined;
  for (const a of cueAddrs(model)) {
    if (a.param !== paramId) continue;
    if (a.y === y) return { addr: a, mirror: false };
    if (a.mirrors.includes(y)) return { addr: a, mirror: true };
  }
  return undefined;
}

// --- Unverified-mapping registry -------------------------------------------
// Device mappings confirmed on URX44V and URX22 that remain educated guesses on
// URX44, pending confirmation by an owner: through
// the device self-test where a round trip answers the guess's question, and by
// reading the unit's own screen where it does not (`roundTripSettles`). Each entry
// is self-contained: it resolves the device
// addresses it writes on a model (so the self-test can tag a finding with the
// guess it confirms or refutes), names the param ids it invents (so the static
// collision audit can vet them), and — when it invents a colliding id — knows how
// to drop its own plan field. Adding a guess is one entry here, no switch to keep
// in sync. Lives in translate.ts because address resolution needs the model
// helpers below (channelControl / stereoIndexMap / channelInputSlots).

/** One device address a guess writes: [paramId, y] (the address x field is 0). */
export type GuessAddress = [paramId: number, y: number];

export interface UnverifiedMapping {
  /** Stable key, also used to tag self-test findings. */
  key: string;
  /** Human-readable description for the self-test report. */
  label: string;
  /** Models on which it is still a guess (confirmed models omitted). */
  models: ModelId[];
  /** Device addresses this guess writes on the model (empty if absent on it). */
  addresses(model: DeviceModel): GuessAddress[];
  /** Whether a round trip answers the guess's question. False where the question is which
   *  instance an address reaches or what a value means there: a unit stores the value the
   *  same way whether the guess is right or wrong, so a run where every address held what
   *  was written reports `roundTripped` rather than `confirmed`. Stated per entry, with no
   *  default, so a new guess has to say which kind it is. */
  roundTripSettles: boolean;
  /** For a guess a round trip does not settle, the observation that does — printed by the
   *  self-test beside the entry, since its run cannot reach a verdict there. */
  howToSettle?: string;
  /** Param ids this guess INVENTS — subject to the collision audit. A guess that
   *  only reuses a confirmed param's id (value/instance guess) leaves this empty. */
  guessedIds: number[];
  /** Strip the plan field a colliding guess would misaddress (omitted = nothing). */
  suppress?(plan: Plan): void;
}

export const UNVERIFIED_MAPPINGS: UnverifiedMapping[] = [
  {
    // The D.Gain address of each digital source on URX44: the URX44V's table, by source. Its
    // sources are a subset of the URX44V's, so the table invents no id; what is unverified is
    // that each address reaches that source's gain on this unit. A value stored and read back
    // says the address keeps one, not which source it feeds.
    key: "dgain-source",
    label: "D.Gain address of each digital input source (the URX44V table: AUX 9, USB MAIN A/B/C 13/14/15, …)",
    models: ["URX44"],
    guessedIds: [],
    roundTripSettles: false,
    howToSettle:
      "write a distinct D.Gain to one source at a time and read which channel's D.Gain the unit's LCD shows for a channel routed to it",
    addresses: (model) =>
      model.nodes.flatMap((node) => {
        const a = D_GAIN_SOURCES[node.id];
        return a ? a.instances.map((y) => [a.id, y] as GuessAddress) : [];
      }),
    suppress: (plan) => {
      for (const id of Object.keys(D_GAIN_SOURCES)) if (plan.nodeParams[id]) delete plan.nodeParams[id].dGain;
    },
  },
  {
    // The analog input stage addressed by jack: +48V, A.Gain and Clip Safe at y = the jack's
    // index, and HI-Z at its HI-Z jacks. Off the factory wiring (CH n reads jack n) the jack
    // and the channel number part, and a wrong axis puts a value on the other physical input.
    key: "jack-axis",
    label: "MIC/LINE input stage addressed by jack (+48V / A.Gain / Clip Safe / HI-Z at y = jack index)",
    models: ["URX44"],
    guessedIds: [],
    roundTripSettles: false,
    howToSettle:
      "route a channel to a MIC/LINE jack other than its own number, write a distinct A.Gain to that jack, and read the value the unit's LCD shows for that channel",
    addresses: (model) => {
      const hiZ = HI_Z_JACKS[model.id] ?? [];
      return model.nodes.flatMap((node) => {
        const base = MIC_LINE_JACK_BASE[node.id];
        if (base === undefined) return [];
        return [base, base + 1].flatMap((j) => [
          [PARAMS.PHANTOM.id, j] as GuessAddress,
          [PARAMS.HA_GAIN.id, j] as GuessAddress,
          [PARAMS.CLIP_SAFE.id, j] as GuessAddress,
          ...(hiZ.includes(j) ? [[PARAMS.HI_Z.id, j] as GuessAddress] : []),
        ]);
      });
    },
  },
  {
    // Two guesses in one, and the round trip only settles the first: whether the
    // invented ids take values at all, and WHICH stereo pair a `y` reaches. The y here
    // is stereoIndexMap's, the same map the ducker block rode, so a faithful device
    // answers CONFIRMED by writing y and reading y back whichever pair y points at.
    // Pair 0 is `ch_5_6` on a URX44/URX44V and `ch_3_4` on a URX22; the private ledger's
    // D-1 carries the sentinel-write and LCD-read procedure that settles the other half,
    // for both this and the ducker block it was written for.
    key: "stereo-block",
    label: "Stereo channel fader/on/pan block (266/267/268)",
    models: ["URX44"],
    guessedIds: [STEREO_FADER, STEREO_ON, STEREO_PAN],
    roundTripSettles: false,
    howToSettle:
      "write a distinct fader level to one stereo index at a time and read on the unit's LCD which stereo channel's fader moved",
    addresses: (model) => {
      const ys = [...stereoIndexMap(model).values()];
      return [STEREO_FADER, STEREO_ON, STEREO_PAN].flatMap((id) => ys.map((y) => [id, y] as GuessAddress));
    },
  },
  {
    // Which physical port each value of param 22 names. A permuted numbering stores and
    // reads back the same, so the round trip says the values are kept, not that each one
    // names the port the plan means. Param 22 carries the MONO IN channels' sources only —
    // a stereo channel's go out as STEREO_INPUT_SOURCE_L/R — so those slots are its
    // addresses.
    key: "input-ports",
    label: "Physical input source port map (param 22 values)",
    models: ["URX44"],
    guessedIds: [],
    roundTripSettles: false,
    howToSettle:
      "point a mono channel at each port value in turn with a signal on one physical input, and read which value moves that channel's input meter",
    addresses: (model) =>
      model.nodes.flatMap((node) =>
        node.kind === "channel" && !isStereoChannel(node.id)
          ? (channelInputSlots(model, node.id) ?? []).map((s) => [PARAMS.INPUT_SOURCE.id, s] as GuessAddress)
          : [],
      ),
  },
  {
    // Which stereo channel and side each block-1 value of the LCD selection names on URX44:
    // 2 × the channel's stereo position, plus 1 for R, as on the URX44V and the URX22. The unit
    // keeps any value written there, so a round trip says nothing about where its LCD moved.
    key: "lcd-side",
    label: "LCD side link: 762 index = 2 × stereo position, +1 for R",
    models: ["URX44"],
    guessedIds: [],
    roundTripSettles: false,
    howToSettle:
      "switch the side of each stereo channel in the app while Live sync is on, and read which channel and side the unit's LCD shows",
    addresses: () => [[PARAMS.LCD_SELECTION.id, 0]],
  },
  {
    // Which strip each [CUE] flag reaches on URX44: the URX44V's addresses, with a channel's
    // flag at the instance its fader takes. The unit keeps any value written there, so a round
    // trip says nothing about which strip's [CUE] lit.
    key: "cue-flags",
    label: "[CUE] flag of each strip (mono CH 138 / stereo CH 265 / FX 336 / STEREO 580 / MIX 673 / STREAMING 709)",
    models: ["URX44"],
    guessedIds: [],
    roundTripSettles: false,
    howToSettle:
      "cue one strip at a time from the app while Live sync is on, and read which strip's [CUE] lights on the unit",
    addresses: (model) => cueAddrs(model).map((a) => [a.param, a.y] as GuessAddress),
  },
];

/** A guessed param id that a confirmed catalog param already owns. */
export interface UnverifiedCollision {
  key: string;
  label: string;
  paramId: number;
  /** The confirmed param the guessed id would actually address. */
  confirmed: ParamName;
}

/**
 * Audit a model's unverified guesses statically (no device needed): a guessed id
 * that a confirmed catalog param already owns is almost certainly wrong — a write
 * meant for the guess would land on that confirmed param instead — so the
 * self-test must not exercise it. Returns one collision per offending id.
 */
export function auditUnverified(model: ModelId): UnverifiedCollision[] {
  const out: UnverifiedCollision[] = [];
  for (const mapping of UNVERIFIED_MAPPINGS) {
    if (!mapping.models.includes(model)) continue;
    for (const id of mapping.guessedIds) {
      const confirmed = paramNameForId(id);
      if (confirmed) out.push({ key: mapping.key, label: mapping.label, paramId: id, confirmed });
    }
  }
  return out;
}

/**
 * Device addresses each still-unverified mapping writes on a model, as
 * "paramId:y" → mapping key (x is always 0 for these). The self-test uses it to
 * tag a residual finding with the guess it confirms or refutes.
 */
export function unverifiedAddresses(model: DeviceModel): Map<string, string> {
  const out = new Map<string, string>();
  for (const mapping of UNVERIFIED_MAPPINGS) {
    if (!mapping.models.includes(model.id)) continue;
    for (const [paramId, y] of mapping.addresses(model)) out.set(`${paramId}:${y}`, mapping.key);
  }
  return out;
}

/** The unverified mapping an address rests on, on this model, or undefined for an address no
 *  guess names. A read or write that fails there is reported against it. */
export function unverifiedGuessAt(model: DeviceModel, paramId: number, y: number): string | undefined {
  return unverifiedAddresses(model).get(`${paramId}:${y}`);
}

/** " (unverified mapping <key>)" for an address a guess names, "" otherwise — the suffix a
 *  failure line in a report carries. */
export function unverifiedGuessNote(model: DeviceModel, paramId: number, y: number): string {
  const key = unverifiedGuessAt(model, paramId, y);
  return key === undefined ? "" : ` (unverified mapping ${key})`;
}
